<template>
  <v-skeleton-loader
    v-if="!profile"
    class="mr-2 mt-6"
    type="list-item-three-line"
  ></v-skeleton-loader>

  <v-form v-else ref="profile_form" lazy-validation>
    <v-row>
      <v-col cols="12" md="12" xs="12">
        <v-text-field
          type="text"
          v-model="profile.profileName"
          :rules="[required($vuetify.lang.t('$vuetify.gpt_profile.name_header_txt'))]"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.name_header_txt')"
          :placeholder="
            $vuetify.lang.t('$vuetify.gpt_profile.profileNamePlaceholder')
          "
          outlined
        ></v-text-field>
      </v-col>
      <v-col>
        <v-select
          v-model="gpt_randomness"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.randomnessPlaceholder')"
          :items="gptRandomnessOptions"
          item-text="name"
          item-value="value"
          :placeholder="
            $vuetify.lang.t('$vuetify.gpt_profile.randomnessPlaceholder')
          "
          outlined
        ></v-select>
      </v-col>
      <v-col cols="12" xs="12">
        <v-textarea
          v-model="profile.intro"
          :rules="[
            required($vuetify.lang.t('$vuetify.gpt_profile.introduction')),
          ]"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.introduction')"
          outlined
          :placeholder="
            $vuetify.lang.t('$vuetify.gpt_profile.introductionPlaceholder')
          "
          type="text"
        ></v-textarea>
      </v-col>
      <v-col cols="12" xs="12">
        <v-textarea
          v-model="profile.system"
          :rules="[required($vuetify.lang.t('$vuetify.gpt_profile.system'))]"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.system')"
          :placeholder="
            $vuetify.lang.t('$vuetify.gpt_profile.systemPlaceholder')
          "
          required
          outlined
        ></v-textarea>
      </v-col>
      <v-col cols="12" xs="12">
        <v-textarea
          v-model="profile.instructions"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.instructions')"
          :placeholder="
            $vuetify.lang.t('$vuetify.gpt_profile.instructionsPlaceholder')
          "
          outlined
        ></v-textarea>
      </v-col>
      <v-col cols="12">
          <v-combobox
            v-model="profile.stop"
            :items="[]"
            :label="$vuetify.lang.t('$vuetify.tooltip.stop')"
            variant="solo"
            small-chips
            clearable
            outlined
            multiple
            closable
            @change="onStopTagsChange"
            :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.stop_placeholder')"
            :hint="$vuetify.lang.t('$vuetify.gpt_profile.stop_sequence')"
            class="no-dropdown-icon"
          >
            <template v-slot:selection="{ attrs, item, select,  selected }">
              <v-chip v-bind="attrs" :model-value="selected" @click="select" @click:close="removeStop(item)" close>
                <span>{{ item }}</span
                >&nbsp;
              </v-chip>
            </template>
          </v-combobox>
        </v-col>
      <v-col cols="12" md="4" xs="12">
        <v-autocomplete
          v-model="profile.provider"
          attach
          :items="gptProviderList"
          item-text="display"
          item-value="provider"
          :label='$vuetify.lang.t("$vuetify.gpt_history.gpt_history_provider")'
          outlined
          required
          :rules="[required($vuetify.lang.t('$vuetify.gpt_history.gpt_history_provider'))]"
          @change="onProviderChange"
        >
          <template v-slot:selection="{ item }">
            <span>{{ item }}</span>
          </template>
          <template v-slot:item="{ item }">
            <span>{{ item }}</span>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" md="4" xs="12">
        <v-autocomplete
          ref="modelInput"
          v-model="profile.model"
          attach
          :items="gptModelList.filter(model => model.provider === profile.provider)"
          item-text="display"
          item-value="model"
          :label='$vuetify.lang.t("$vuetify.gpt_profile.model")'
          outlined
          required
          :rules="[required($vuetify.lang.t('$vuetify.gpt_profile.model'))]"
          :error-messages="modelFieldError"
          :loading="modelDetailsStatus === 'loading'"
          :filter="customFilter"
          :menu-props="{ maxHeight: '400px' }"
          @change="onModelChange"
        >
          <template v-slot:selection="{ item }">
            <span>{{ item.model }}</span>
          </template>
          <template v-slot:item="{ item }">
            <span>{{ item.model }}</span>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" md="4" xs="12">
        <v-select
          v-model="profile.tools"
          :items="modelTools"
          :label='$vuetify.lang.t("$vuetify.gpt_profile.tools")'
          attach
          outlined
          chips
          small-chips
          deletable-chips
          clearable
          multiple
          :menu-props="{ maxHeight: '400px', offsetY: true }"
          :disabled="!modelTools.length"
          @change="onToolsChange"
        >
          <template v-slot:item="{ item }">
            <div class="d-flex align-center justify-space-between w-100">
              <span>{{ item.text }}</span>
              <v-tooltip bottom max-width="300">
                <template v-slot:activator="{ on, attrs }">
                <span
                  class="info-icon"
                  v-bind="attrs"
                  v-on="on"
                >
                  i
                </span>
                </template>

                <span>
                  {{ toolItemHintText(item.value) }}
                </span>
              </v-tooltip>

            </div>
          </template>
          <!-- Field Info Icon -->
          <template v-slot:append>
            <v-tooltip bottom max-width="350">
              <template v-slot:activator="{ on, attrs }">
                <span
                  class="info-icon"
                  v-bind="attrs"
                  v-on="on"
                >
                  i
                </span>
              </template>
              <span>{{ toolsTooltipText }}</span>
            </v-tooltip>
          </template>
        </v-select>
      </v-col>
      <v-col cols="12" md="6" xs="12">
        <v-text-field
          ref="temperatureField"
          v-model="profile.temperature"
          :rules="dynamicTemperatureRules"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.temperature')"
          outlined
          type="number"
          step="0.1"
          :min="temperatureMeta.min"
          :max="temperatureMeta.max"
          :loading="modelDetailsStatus === 'loading'"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          ref="topPField"
          v-model="profile.top_p"
          :rules="dynamicTopPRules"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.top_p')"
          outlined
          type="number"
          step="0.1"
          :min="topPMeta.min"
          :max="topPMeta.max"
          :loading="modelDetailsStatus === 'loading'"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="profile.top_k"
          :rules="topKRules"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.top_k')"
          outlined
          type="number"
          min="1"
          max="50"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="profile.frequencyPenalty"
          :rules="penaltyRules"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.frequency_penalty')"
          outlined
          type="number"
          step="0.1"
          min="0"
          max="2"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="profile.presencePenalty"
          :rules="penaltyRules"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.presence_penalty')"
          outlined
          type="number"
          step="0.1"
          min="0"
          max="2"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="profile.searchMaxToken"
          :rules="searchTokenRules"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.search_max_token')"
          outlined
          type="number"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="profile.completionToken"
          :rules="completionTokenRules"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.completion_token')"
          outlined
          type="number"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="profile.chat_history_length"
          :rules="chatHistoryLengthRules"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.chat_history_length')"
          outlined
          type="number"
        ></v-text-field>
      </v-col>

      <v-col cols="12" md="6">
        <v-select
          v-model="profile.is_personalizer_only"
          :items="personalizerOnlyItems"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.is_personalizer_only')"
          outlined
        ></v-select>
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          v-model="profile.is_auto_evaluation"
          :items="isAutoEvaluation"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.is_auto_evaluation')"
          outlined
        ></v-select>
      </v-col>
      <v-col cols="12" md="6" xs="12">
        <v-select
          v-model="profile.search_mode"
          :items="search_mode"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.search_mode')"
          outlined
        ></v-select>
      </v-col>
      <v-col cols="6" md="3" xs="12">
        <v-combobox
          v-model="profile.dataset_languages"
          :items="['en', 'ja', 'ko']"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.dataset_languages')"
          variant="solo"
          small-chips
          clearable
          outlined
          multiple
          hide-no-data
          no-filter
          @keydown.prevent
        >
        <template v-slot:selection="{ attrs, item, select, selected }">
          <v-chip
            v-bind="attrs" 
            @click="select" 
            :model-value="selected" 
            @click:close="removeLanguage(item)" 
            closable
            close
          >
            <span>{{ getFullFormOfLanguage(item) }}</span>
          </v-chip>
        </template>
        <template v-slot:item="{ item }">
          <span>{{ getFullFormOfLanguage(item) }}</span>
        </template>
        </v-combobox>
      </v-col>
      <v-col cols="6" md="3" xs="12">
        <v-select
          v-model="profile.is_lang_auto_detect"
          :items="isLangAutoDetect"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.is_lang_auto_detect')"
          outlined
        ></v-select>
      </v-col>
      <v-col cols="12" md="12">
        <v-combobox
          v-model="profile.access_tags"
          :items="[]"
          :label="$vuetify.lang.t('$vuetify.access_tab.access_tags')"
          variant="solo"
          chips
          clearable
          outlined
          multiple
          @change="onProfileTagsChange"
          :hint="$vuetify.lang.t('$vuetify.gpt_profile.chip_hint')"
          class="no-dropdown-icon"
        >
          <template v-slot:selection="{ attrs, item, select, selected }">
            <v-chip v-bind="attrs" @click="select"  @click:close="removeAccessTags(item)" :model-value="selected" closable close>
              <span>{{ item }}</span
              >&nbsp;
            </v-chip>
          </template>
        </v-combobox>
      </v-col>

      <v-col cols="12" md="12">
        <v-row class="align-center">
          <v-col cols="12" md="12" class="d-flex align-center pb-4">
            <v-checkbox
              hide-details
              class="ma-0 pa-0"
              :ripple="false"
              v-model="profile.is_citation_enabled"
              :label="$vuetify.lang.t('$vuetify.gpt_profile.is_citation_enabled')"
            ></v-checkbox>
            <v-tooltip bottom max-width="450">
              <template v-slot:activator="{ on, attrs }">
                <span v-on="on" v-bind="attrs" class="info-icon">i</span>
              </template>
              <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.is_citation_enabled_hint') }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12">
        <p>{{ $vuetify.lang.t('$vuetify.gpt_profile.office_hours_and_session_management') }}:</p>
        <div class="info-notice mt-2 pl-1 d-flex align-center flex-wrap">
          <v-icon small color="info" class="mr-1">mdi-information</v-icon>
          <span class="info-notice-text">{{ $vuetify.lang.t('$vuetify.gpt_profile.office_hours_info') }}</span>
        </div>
      </v-col>

     <v-col cols="12" md="6">
        <v-menu
          ref="startTimeMenu"
          v-model="startTimeMenu"
          :close-on-content-click="false"
          :nudge-right="40"
          :return-value.sync="profile.office_hour_start"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="profile.office_hour_start"
              :label="$vuetify.lang.t('$vuetify.gpt_profile.office_hour_start')"
              placeholder="HH:MM (e.g., 09:00)"
              :rules="[!profile.office_hour_start || !profile.office_hour_end 
                || profile.office_hour_start !== profile.office_hour_end || $vuetify.lang.t('$vuetify.gpt_profile.office_hour_start_validation')]"
              readonly
              outlined
              v-bind="attrs"
              v-on="on"
              clearable
            >
            <template v-slot:append>
              <v-tooltip bottom max-width="350">
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="info-icon">i</span>
                </template>
                <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.office_hour_start_hint') }}</span>
              </v-tooltip>
            </template>
          </v-text-field>
          </template>
          <v-card>
            <v-time-picker
            v-if="startTimeMenu"
            v-model="profile.office_hour_start"
            full-width
            scrollable
            format="24hr"
            ></v-time-picker>
            <v-card-actions class="d-flex justify-end">
              <v-btn text color="error" @click="startTimeMenu = false">
                {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
              </v-btn>
              <v-btn text color="primary" @click="$refs.startTimeMenu.save(profile.office_hour_start)">
                {{ $vuetify.lang.t("$vuetify.common.ok_btn") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </v-col>
      
      <v-col cols="12" md="6">
        <v-menu
          ref="endTimeMenu"
          v-model="endTimeMenu"
          :close-on-content-click="false"
          :nudge-right="40"
          :return-value.sync="profile.office_hour_end"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="profile.office_hour_end"
              :label="$vuetify.lang.t('$vuetify.gpt_profile.office_hour_end')"
              placeholder="HH:MM (e.g., 17:00)"
              :rules="[!profile.office_hour_end || !profile.office_hour_start 
                || profile.office_hour_end !== profile.office_hour_start || $vuetify.lang.t('$vuetify.gpt_profile.office_hour_end_validation')]"
              readonly
              outlined
              v-bind="attrs"
              v-on="on"
              clearable
            >
           <template v-slot:append>
              <v-tooltip bottom max-width="350">
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="info-icon">i</span>
                </template>
                <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.office_hour_end_hint') }}</span>
              </v-tooltip>
            </template>
          </v-text-field>
          </template>
          <v-card>
            <v-time-picker
            v-if="endTimeMenu"
            v-model="profile.office_hour_end"
            full-width
            scrollable
            format="24hr"
            ></v-time-picker>
            <v-card-actions class="d-flex justify-end">
              <v-btn text color="red" @click="endTimeMenu = false">
                {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
              </v-btn>
              <v-btn text color="primary" @click="$refs.endTimeMenu.save(profile.office_hour_end)">
                {{ $vuetify.lang.t("$vuetify.common.ok_btn") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </v-col>
      
      
      <v-col cols="12">
        <div class="textarea-with-tooltip">
          <v-textarea
          v-model="profile.out_of_office_message"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.out_of_office_message')"
          outlined
          rows="2"
          ></v-textarea>
          <v-tooltip bottom max-width="350">
            <template v-slot:activator="{ on }">
              <span v-on="on" class="info-icon">i</span>
            </template>
            <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.out_of_office_message_hint') }}</span>
          </v-tooltip>
        </div>
      </v-col>
      
      <v-col cols="12" md="6">
        <v-text-field
          v-model="profile.max_concurrent_sessions"
          :rules="[positiveIntegerRule]"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.max_concurrent_sessions')"
          outlined
          type="number"
          min="1"
        >
        <template v-slot:append>
          <v-tooltip bottom max-width="350">
            <template v-slot:activator="{ on }">
              <span v-on="on" class="info-icon">i</span>
            </template>
            <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.max_concurrent_sessions_hint') }}</span>
          </v-tooltip>
        </template>
      </v-text-field>
      </v-col>
      
      <v-col cols="12" md="6">
        <v-text-field
          v-model="profile.session_timeout_minutes"
          :rules="[positiveIntegerRule]"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.session_timeout_minutes')"
          outlined
          type="number"
          min="1"
        >
        <template v-slot:append>
          <v-tooltip bottom max-width="350">
            <template v-slot:activator="{ on }">
              <span v-on="on" class="info-icon">i</span>
            </template>
            <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.session_timeout_minutes_hint') }}</span>
          </v-tooltip>
        </template>
      </v-text-field>
      </v-col>
      
      <v-col cols="6">
        <div class="textarea-with-tooltip">
          <v-textarea
          v-model="profile.session_limit_reached_message"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.session_limit_reached_message')"
          outlined
          rows="2"
          ></v-textarea>
          <v-tooltip bottom max-width="350">
            <template v-slot:activator="{ on }">
              <span v-on="on" class="info-icon">i</span>
            </template>
            <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.session_limit_reached_message_hint') }}</span>
          </v-tooltip>
        </div>
      </v-col>

      <v-col cols="6">
        <div class="textarea-with-tooltip">
          <v-textarea
          v-model="profile.session_already_finished_message"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.session_already_finished_message')"
          outlined
          rows="2"
          ></v-textarea>
          <v-tooltip bottom max-width="350">
            <template v-slot:activator="{ on }">
              <span v-on="on" class="info-icon">i</span>
            </template>
            <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.session_already_finished_message_hint') }}</span>
          </v-tooltip>
        </div>
      </v-col>

      <v-col cols="12">
        <p>{{ $vuetify.lang.t("$vuetify.gpt_profile.dataset_tags") }}:</p>
      </v-col>
      <v-col cols="12" md="12">
      <v-combobox
        v-model="profile.datasetTags.includes"
        :items="[]"
        :label="$vuetify.lang.t('$vuetify.gpt_profile.includes')"
        variant="solo"
        chips
        clearable
        outlined
        multiple
        @change="includesTagsChange"
        @keydown="handleKeydown"
        :hint="!showtags ? $vuetify.lang.t('$vuetify.gpt_profile.chip_hint') : ''"
        :class="{ 'no-dropdown-icon': !showtags }"
      >
        <!-- Custom dropdown list -->
        <template v-slot:no-data>
          <div v-if="showtags" class="px-4 py-2">
            <!-- Headers -->
            <div class="d-flex mb-3 pb-2 header-row">
              <div class="dataset-name-column text-subtitle-1 font-weight-bold">{{$vuetify.lang.t('$vuetify.gpt_dataset.datasets')}}</div>
              <div class="tags-column text-subtitle-1 font-weight-bold">{{$vuetify.lang.t('$vuetify.access_tab.tags')}}</div>
            </div>
            
            <!-- Data rows -->
            <div 
              v-for="datasetItem in formattedTagsItemsForInclude" 
              :key="datasetItem.dataset" 
              class="d-flex mb-3 pb-2 data-row"
              :class="{
                'disabled-dataset': datasetDisabledForIncludes(datasetItem.dataset)
              }"
            >
              <div class="dataset-name-column">
                <v-tooltip bottom v-if="datasetNameTruncated(datasetItem.dataset)">
                  <template v-slot:activator="{ on, attrs }">
                    <div 
                      class="text-subtitle-2 font-weight-bold dataset-name-text"
                      :class="{
                        'disabled-text': datasetDisabledForIncludes(datasetItem.dataset)
                      }"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ truncateDatasetName(datasetItem.dataset) }}:
                    </div>
                  </template>
                  <span class="tooltip-text">{{ (datasetItem.dataset || '').toUpperCase() }}</span>
                </v-tooltip>
                
                <div 
                  v-else
                  class="text-subtitle-2 font-weight-bold dataset-name-text"
                  :class="{
                    'disabled-text': datasetDisabledForIncludes(datasetItem.dataset)
                  }"
                >
                  {{ truncateDatasetName(datasetItem.dataset) }}:
                </div>
              </div>
              
              <div class="tags-column">
                <div class="tag-badges">
                  <template v-if="datasetItem.tags.length">
                    <v-chip
                      v-for="tag in datasetItem.tags"
                      :key="`${datasetItem.dataset}-${tag}`"
                      small
                      class="mr-1 mb-1"
                      :class="{
                        'clickable': canIncludeTag(tag) && !datasetDisabledForIncludes(datasetItem.dataset),
                        'disabled-tag': !canIncludeTag(tag) || datasetDisabledForIncludes(datasetItem.dataset),
                        'contaminated-tag': tagContaminated(tag, 'include')
                      }"
                      :color="getTagColor(tag, 'dropdown')"
                      :style="{
                        cursor: getTagCursor(tag, 'include', datasetItem.dataset),
                        opacity: getTagOpacity(tag, 'include', datasetItem.dataset)
                      }"
                      @click.stop="datasetDisabledForIncludes(datasetItem.dataset) ? null : toggleIncludeTag(tag)"
                      :disabled="!canIncludeTag(tag) || datasetDisabledForIncludes(datasetItem.dataset)"
                    >
                      {{ tag }}
                      
                      <!-- Show different icons based on tag state -->
                      <v-icon 
                        v-if="tagExcluded(tag)" 
                        small 
                        class="ml-1"
                        color="error"
                      >
                        mdi-minus-circle
                      </v-icon>
                      
                      <!-- Show contamination warning for contaminated tags -->
                      <!-- <v-icon 
                        v-else-if="tagContaminated(tag, 'include')" 
                        small 
                        class="ml-1"
                        color="warning"
                      >
                        mdi-alert-circle
                      </v-icon> -->
                    </v-chip>
                  </template>
                  <template v-else>
                    <span class="text-caption grey--text">
                      {{ $vuetify.lang.t("$vuetify.gpt_profile.no_tags_found") }}
                    </span>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Selection display with closable chips -->
        <template v-slot:selection="{ item }">
          <v-chip
            close
            class="ma-1"
            color="primary"
            @click:close="removeIncludes(item)"
          >
            {{ item }}
          </v-chip>
        </template>
      </v-combobox>
    </v-col>
    
    <v-col cols="12" md="12">
      <v-combobox
        v-model="profile.datasetTags.excludes"
        :items="[]"
        :label="$vuetify.lang.t('$vuetify.gpt_profile.excludes')"
        variant="solo"
        chips
        clearable
        outlined
        multiple
        @change="excludesTagsChange"
        @keydown="handleKeydown"
        :hint="!showtags ? $vuetify.lang.t('$vuetify.gpt_profile.chip_hint') : ''"
        :class="{ 'no-dropdown-icon': !showtags }"
      >
        <!-- Custom dropdown list-->
        <template v-slot:no-data>
          <div v-if="showtags" class="px-4 py-2">
            <!-- Headers -->
            <div class="d-flex mb-3 pb-2 header-row">
              <div class="dataset-name-column text-subtitle-1 font-weight-bold">{{$vuetify.lang.t('$vuetify.gpt_dataset.datasets')}}</div>
              <div class="tags-column text-subtitle-1 font-weight-bold">{{$vuetify.lang.t('$vuetify.access_tab.tags')}}</div>
            </div>
            
            <!-- Data rows -->
            <div 
              v-for="datasetItem in formattedTagsItemsForExclude" 
              :key="datasetItem.dataset" 
              class="d-flex mb-3 pb-2 data-row"
              :class="{
                'disabled-dataset': datasetDisabledForExcludes(datasetItem.dataset)
              }"
            >
              <div class="dataset-name-column">
                <v-tooltip bottom v-if="datasetNameTruncated(datasetItem.dataset)">
                  <template v-slot:activator="{ on, attrs }">
                    <div 
                      class="text-subtitle-2 font-weight-bold dataset-name-text"
                      :class="{
                        'disabled-text': datasetDisabledForExcludes(datasetItem.dataset)
                      }"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ truncateDatasetName(datasetItem.dataset) }}:
                    </div>
                  </template>
                  <span class="tooltip-text">{{ (datasetItem.dataset || '').toUpperCase() }}</span>
                </v-tooltip>
                
                <div 
                  v-else
                  class="text-subtitle-2 font-weight-bold dataset-name-text"
                  :class="{
                    'disabled-text': datasetDisabledForExcludes(datasetItem.dataset)
                  }"
                >
                  {{ truncateDatasetName(datasetItem.dataset) }}:
                </div>
              </div>
              
              <div class="tags-column">
                <div class="tag-badges">
                  <template v-if="datasetItem.tags.length">
                    <v-chip
                      v-for="tag in datasetItem.tags"
                      :key="`${datasetItem.dataset}-${tag}`"
                      small
                      class="mr-1 mb-1"
                      :class="{
                        'clickable': canExcludeTag(tag) && !datasetDisabledForExcludes(datasetItem.dataset),
                        'disabled-tag': !canExcludeTag(tag) || datasetDisabledForExcludes(datasetItem.dataset),
                        'contaminated-tag': tagContaminated(tag, 'exclude')
                      }"
                      :color="getTagColor(tag, 'dropdown')"
                      :style="{
                        cursor: getTagCursor(tag, 'exclude', datasetItem.dataset),
                        opacity: getTagOpacity(tag, 'exclude', datasetItem.dataset)
                      }"
                      @click.stop="datasetDisabledForExcludes(datasetItem.dataset) ? null : toggleExcludeTag(tag)"
                      :disabled="!canExcludeTag(tag) || datasetDisabledForExcludes(datasetItem.dataset)"
                    >
                      {{ tag }}
                      
                      <!-- Show different icons based on tag state -->
                      <v-icon 
                        v-if="tagIncluded(tag)" 
                        small 
                        class="ml-1"
                        color="primary"
                      >
                        mdi-plus-circle
                      </v-icon>
                      
                      <!-- Show contamination warning for contaminated tags -->
                      <!-- <v-icon 
                        v-else-if="tagContaminated(tag, 'exclude')" 
                        small 
                        class="ml-1"
                        color="warning"
                      >
                        mdi-alert-circle
                      </v-icon> -->
                    </v-chip>
                  </template>
                  <template v-else>
                    <span class="text-caption grey--text">
                      {{ $vuetify.lang.t("$vuetify.gpt_profile.no_tags_found") }}
                    </span>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Selection display with closable chips -->
        <template v-slot:selection="{ item }">
          <v-chip
            close
            class="ma-1"
            color="error"
            @click:close="removeExcludes(item)"
          >
            {{ item }}
          </v-chip>
        </template>
      </v-combobox>
    </v-col>
    </v-row>
    <p v-if="isVisionSupported">
      {{ $vuetify.lang.t("$vuetify.gpt_history.gpt_history_vision_settings") }}:
    </p>
    <v-select
      v-if="isVisionSupported"
      v-model="profile.visionSettings.resolution"
      :items="['low', 'high']"
      :label="$vuetify.lang.t('$vuetify.gpt_history.gpt_history_resolution')"
      outlined
      required
      :rules="[required($vuetify.lang.t('$vuetify.gpt_history.gpt_history_resolution'))]"
    ></v-select>
    <v-row v-if="isVisionSupported" class="align-center ms-1 py-1">
      <v-checkbox
        hide-details
        class="ma-0 pa-0"
        :ripple="false"
        v-model="profile.visionSettings.imageContextEnabled"
        :label="$vuetify.lang.t('$vuetify.gpt_history.gpt_history_allow_file_uploads')"
        ></v-checkbox>
      <v-tooltip bottom max-width="450">
        <template v-slot:activator="{ on, attrs }">
          <span v-on="on" v-bind="attrs" class="info-icon">i</span>
        </template>

        <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.gpt_history_allow_file_uploads_hint') }}</span>
      </v-tooltip>
    </v-row>
    <v-row v-if="isVisionSupported" class="align-center ms-1 pt-1 pb-5">
    <v-checkbox
      hide-details
      class="ma-0 pa-0"
      :ripple="false"
      v-if="isVisionSupported"
      v-model="profile.visionSettings.isConversationLearning"
      :label="$vuetify.lang.t('$vuetify.gpt_history.gpt_history_allow_file_to_be_added_to_knowledge_base')"
    ></v-checkbox>
    <v-tooltip bottom max-width="450">
        <template v-slot:activator="{ on, attrs }">
          <span v-on="on" v-bind="attrs" class="info-icon">i</span>
        </template>

        <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.conversation_learning_hint') }}</span>
      </v-tooltip>
      </v-row>
    
    <template v-if="isJsonSupported">
      <v-row>
        <v-col cols="12">
          <p>{{ $vuetify.lang.t("$vuetify.gpt_profile.json_mode_settings") }}</p>
        </v-col>
        <v-col cols="12" md="12">
          <v-select
            v-model="profile.responseFormat"
            :items="filteredResponseFormatOptions"
            label="Response Format"
            outlined
            class="mt-n4"
          ></v-select>
        </v-col>
      </v-row>
      <v-row v-if="profile.responseFormat === 'json_object'">
        <v-col cols="12">
          <v-alert
            type="info"
            outlined
            class="mt-n10">
            {{ $vuetify.lang.t("$vuetify.gpt_profile.json_warning") }}
          </v-alert>
        </v-col>
      </v-row>
    </template>
    <template>
    <v-row class="align-center">
      <v-col cols="12" md="12" class="d-flex align-center pb-4">
          <v-checkbox
            hide-details
            class="ma-0 pa-0"
            :ripple="false"
            v-model="profile.isConversationAdding"
            :label="$vuetify.lang.t('$vuetify.gpt_history.gpt_history_allow_conversation_to_be_added_to_knowledge_base')"
          ></v-checkbox>
          <v-tooltip bottom max-width="450">
              <template v-slot:activator="{ on, attrs }">
                <span v-on="on" v-bind="attrs" class="info-icon">i</span>
              </template>
              <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.conversation_adding_hint') }}</span>
            </v-tooltip>
        </v-col>
    </v-row>
    <v-row class="align-center">
      <v-col cols="12" md="12" class="d-flex align-center flex-wrap pb-4">
          <v-checkbox
            hide-details
            class="ma-0 pa-0"
            :ripple="false"
            v-model="profile.isBackgroundAsk"
            :label="$vuetify.lang.t('$vuetify.gpt_history.gpt_history_is_background_ask')"
            :rules="isBackgroundAskRequired
              ? [value => value || $vuetify.lang.t('$vuetify.gpt_profile.background_ask_required_message')]
              : []"
          ></v-checkbox>
          <v-tooltip bottom max-width="450">
              <template v-slot:activator="{ on, attrs }">
                <span v-on="on" v-bind="attrs" class="info-icon">i</span>
              </template>
              <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.background_ask_hint') }}</span>
            </v-tooltip>
        <div v-if="isBackgroundAskRequired && !profile.isBackgroundAsk" class="error--text text-caption mt-1 background-ask-error">
          {{ $vuetify.lang.t('$vuetify.gpt_profile.background_ask_required_message') }}
        </div>
        </v-col>
    </v-row>
    <v-row class="align-center">
      <v-col cols="12" md="12" class="d-flex align-center pb-4">
          <v-checkbox
            hide-details
            class="ma-0 pa-0"
            :ripple="false"
            v-model="profile.session_close_trigger_config.is_enabled"
            :label="$vuetify.lang.t('$vuetify.gpt_profile.session_close_trigger_config_txt')"
          ></v-checkbox>
          <v-tooltip bottom max-width="450">
              <template v-slot:activator="{ on, attrs }">
                <span v-on="on" v-bind="attrs" class="info-icon">i</span>
              </template>
              <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.session_close_trigger_config_txt_hint') }}</span>
            </v-tooltip>
        </v-col>
    </v-row>
    <v-row v-if="profile.session_close_trigger_config.is_enabled">
      <v-col cols="12" md="12">
        <v-combobox
          v-model="profile.session_close_trigger_config.triggers"
          :items="[]"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.session_close_triggers_txt')"
          chips
          clearable
          outlined
          multiple
          @change="onSessionCloseTriggersChange"
          :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.session_close_triggers_chip_hint')"
          class="no-dropdown-icon"
        >
          <template v-slot:selection="{ attrs, item, select, selected }">
            <v-chip
              v-bind="attrs"
              :model-value="selected"
              class="url-chip"
              @click="select"
              @click:close="removeSessionCloseTrigger(item)"
              close
              closable
            >
              <span class="thin-scroll">{{ item }}</span>
            </v-chip>
          </template>
          <template v-slot:append>
            <v-tooltip bottom max-width="350">
              <template v-slot:activator="{ on }">
                <div v-on="on" class="info-icon-wrapper">
                  <span class="info-icon">i</span>
                </div>
              </template>
              <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.session_close_triggers_txt_hint') }}</span>
            </v-tooltip>
          </template>
        </v-combobox>
      </v-col>
    </v-row>
    </template>
    <template>
      <div class="time-series-section">
        <v-row>
          <v-col cols="12">
            <v-divider class="my-4"></v-divider>
            <v-row align="center" class="mb-4 mt-0">
              <v-col cols="12" sm="auto" class="d-flex align-center py-1">
                <p class="text-h6 mb-0">
                  {{ $vuetify.lang.t('$vuetify.gpt_profile.time_series_config.title') }}
                </p>

                <v-tooltip top max-width="350">
                  <template #activator="{ on, attrs }">
                    <span
                      v-on="on"
                      v-bind="attrs"
                      class="info-icon ml-2"
                    >i</span>
                  </template>
                  <span>
                    {{ $vuetify.lang.t('$vuetify.gpt_profile.time_series_config.tooltip_text') }}
                  </span>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row class="mb-4">
          <v-col cols="12">
            <div class="d-flex align-center">
              <v-checkbox
                v-model="profile.time_series_config.require_user_confirmation"
                :label="$vuetify.lang.t('$vuetify.gpt_profile.time_series_config.require_user_confirmation_label')"
                color="primary"
                class="ma-0 pa-0 flex-grow-0 no-hover"
                :ripple="false"
                hide-details
              ></v-checkbox>

              <v-tooltip top max-width="350">
                <template #activator="{ on, attrs }">
                  <span v-on="on" v-bind="attrs" class="info-icon ml-2">i</span>
                </template>
                <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.time_series_config.require_user_confirmation_tooltip') }}</span>
              </v-tooltip>
            </div>
          </v-col>
        </v-row>

        <v-row class="mb-4">
          <v-col cols="12">
            <div class="d-flex align-center">
              <v-checkbox
                v-model="profile.time_series_config.show_reference_fields"
                :label="$vuetify.lang.t('$vuetify.gpt_profile.time_series_config.show_reference_fields_label')"
                color="primary"
                class="ma-0 pa-0 flex-grow-0 no-hover"
                :ripple="false"
                hide-details
              ></v-checkbox>

              <v-tooltip top max-width="350">
                <template #activator="{ on, attrs }">
                  <span v-on="on" v-bind="attrs" class="info-icon ml-2">i</span>
                </template>
                <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.time_series_config.show_reference_fields_tooltip') }}</span>
              </v-tooltip>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="profile.time_series_config.default_timezone"
              :items="timezoneOptions"
              :label="$vuetify.lang.t('$vuetify.gpt_profile.time_series_config.default_timezone_label')"
              :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.time_series_config.default_timezone_placeholder')"
              :no-data-text="$vuetify.lang.t('$vuetify.gpt_profile.time_series_config.default_timezone_no_data')"
              attach
              outlined
              clearable
              dense
            >
              <template #append-outer>
                <v-tooltip top max-width="350">
                  <template #activator="{ on, attrs }">
                    <div v-on="on" v-bind="attrs" class="info-icon-wrapper">
                      <span class="info-icon">i</span>
                    </div>
                  </template>
                  <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.time_series_config.default_timezone_tooltip') }}</span>
                </v-tooltip>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
      </div>

      <div v-if="showConfig">
        <v-row>
          <v-col cols="12">
            <v-divider class="my-4"></v-divider>
            <v-row align="center" class="mb-2 mt-0">
              <v-col cols="12" sm="auto" class="d-flex align-center py-1">
                <p class="text-h6 mb-0">{{ $vuetify.lang.t("$vuetify.profile.questionnaire_config_header_txt") }}</p>
              </v-col>
              <v-col cols="12" sm="" class="d-flex justify-sm-end py-1">
                <v-btn
                  color="primary"
                  @click="addQuestionnaireConfig"
                >
                  <v-icon small left>mdi-plus</v-icon>
                  {{ $vuetify.lang.t("$vuetify.profile.add_config_btn_txt") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <div>
          <template v-if="profile.questionnaire_config && profile.questionnaire_config.length > 0">
            <div class="d-flex">
              <v-expansion-panels v-model="expandedPanels">
                <v-expansion-panel
                  v-for="(config, index) in profile.questionnaire_config"
                  :key="`questionnaire-${index}`"
                >
                  <v-expansion-panel-header>
                    <div class="d-flex justify-space-between align-center w-100">
                    <span class="text-subtitle-1">{{ getPanelTitle(config, index) }}</span>
                    <v-spacer></v-spacer>
                    <v-btn
                      icon
                      small
                      color="error"
                      @click.stop="confirmDeleteConfig(index)"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content>
                    <v-row>
                      <!-- Questionnaire ID -->
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="config.questionnaire_id"
                          :items="getAvailableQuestionnaireOptions(config)"
                          :rules="[required($vuetify.lang.t('$vuetify.maira_questionnaire.questionnaire_name_required_message'))]"
                          :label="$vuetify.lang.t('$vuetify.maira_questionnaire.questionnaire_name')"
                          :placeholder="$vuetify.lang.t('$vuetify.profile.questionnaire_id_placeholder_txt')"
                          item-text="label"
                          item-value="id"
                          outlined
                          clearable
                        >
                        <template v-slot:append>
                          <v-tooltip bottom max-width="350">
                            <template v-slot:activator="{ on }">
                              <span v-on="on" class="info-icon">i</span>
                            </template>
                            <span>{{ $vuetify.lang.t("$vuetify.maira_questionnaire.select_questionnaire_tooltip") }}</span>
                          </v-tooltip>
                        </template>
                        </v-select>
                      </v-col>

                      <!-- Display Rule -->
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="config.display_rule"
                          :items="displayRuleOptions"
                          :rules="[required($vuetify.lang.t('$vuetify.maira_questionnaire.display_rule'))]"
                          item-text="text"
                          item-value="value"
                          :label="$vuetify.lang.t('$vuetify.maira_questionnaire.display_rule')"
                          outlined
                          required
                        >
                        <template v-slot:append>
                          <v-tooltip bottom max-width="350">
                            <template v-slot:activator="{ on }">
                              <span v-on="on" class="info-icon">i</span>
                            </template>
                            <span>{{ $vuetify.lang.t("$vuetify.maira_questionnaire.display_rule_tooltip") }}</span>
                          </v-tooltip>
                        </template>
                        </v-select>
                      </v-col>

                      <!-- Order -->
                      <v-col cols="12" md="6">
                        <v-text-field
                          type="number"
                          v-model.number="config.order"
                          :label="$vuetify.lang.t('$vuetify.maira_questionnaire.order')"
                          :placeholder="$vuetify.lang.t('$vuetify.profile.order_placeholder_txt')"
                          outlined
                          min="1"
                          :rules="orderRules"
                        >
                        <template v-slot:append>
                          <v-tooltip bottom max-width="350">
                            <template v-slot:activator="{ on }">
                              <span v-on="on" class="info-icon">i</span>
                            </template>
                            <span>{{ $vuetify.lang.t("$vuetify.maira_questionnaire.order_tooltip") }}</span>
                          </v-tooltip>
                        </template>
                        </v-text-field>
                      </v-col>

                      <!-- Active Status -->
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="config.active"
                          :items="active"
                          outlined
                          :label="$vuetify.lang.t('$vuetify.dashboard.active')"
                        >
                        <template v-slot:append>
                          <v-tooltip bottom max-width="350">
                            <template v-slot:activator="{ on }">
                              <span v-on="on" class="info-icon">i</span>
                            </template>
                            <span>{{ $vuetify.lang.t("$vuetify.maira_questionnaire.active_tooltip") }}</span>
                          </v-tooltip>
                        </template>
                        </v-select>
                      </v-col>

                      <!-- Triggers -->
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="config.triggers"
                          :items="triggersOptions"
                          :rules="triggersRules"
                          item-text="text"
                          item-value="value"
                          :label="$vuetify.lang.t('$vuetify.maira_questionnaire.triggers')"
                          outlined
                          multiple
                          chips
                          small-chips
                        >
                        <template v-slot:append>
                          <v-tooltip bottom max-width="350">
                            <template v-slot:activator="{ on }">
                              <span v-on="on" class="info-icon">i</span>
                            </template>
                            <span>{{ $vuetify.lang.t("$vuetify.maira_questionnaire.triggers_tooltip") }}</span>
                          </v-tooltip>
                        </template>
                        </v-select>
                      </v-col>

                      <!-- Use Intelligent Detection -->
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="config.use_intelligent_detection"
                          :items="use_intelligent_detection"
                          outlined
                          :label="$vuetify.lang.t('$vuetify.maira_questionnaire.use_intelligent_detection')"
                          :disabled="!isKeywordDetected(config.triggers)"
                        >
                        <template v-slot:append>
                          <v-tooltip bottom max-width="350">
                            <template v-slot:activator="{ on }">
                              <span v-on="on" class="info-icon">i</span>
                            </template>
                            <span>{{ $vuetify.lang.t("$vuetify.maira_questionnaire.use_intelligent_detection_tooltip") }}</span>
                          </v-tooltip>
                        </template>
                        </v-select>
                      </v-col>

                      <!-- Keywords -->
                      <v-col cols="12">
                        <v-combobox
                          v-model="config.keywords"
                          :disabled="!isKeywordDetected(config.triggers)"
                          :items="[]"
                          :rules="keywordsRules(config)"
                          :label="$vuetify.lang.t('$vuetify.maira_questionnaire.keywords')"
                          outlined
                          multiple
                          chips
                          small-chips
                          clearable
                          @change="onKeywordsChange(index, $event)"
                          :hint="$vuetify.lang.t('$vuetify.profile.keyword_hint_txt')"
                          class="no-dropdown-icon"
                        >
                          <template v-slot:selection="{ attrs, item, select, selected }">
                            <v-chip
                              v-bind="attrs"
                              @click="select"
                              @click:close="removeKeyword(index, item)"
                              :model-value="selected"
                              close
                              small
                              :disabled="!isKeywordDetected(config.triggers)"
                            >
                              <span>{{ item }}</span>
                            </v-chip>
                          </template>
                          <template v-slot:append>
                            <v-tooltip bottom max-width="350">
                              <template v-slot:activator="{ on }">
                                <span v-on="on" class="info-icon">i</span>
                              </template>
                              <span>{{ $vuetify.lang.t("$vuetify.maira_questionnaire.keywords_tooltip") }}</span>
                            </v-tooltip>
                          </template>
                        </v-combobox>
                      </v-col>

                      <!-- Intro Message -->
                      <v-col cols="12">
                        <div class="textarea-with-tooltip">
                          <v-textarea
                            v-model="config.intro_message"
                            :label="$vuetify.lang.t('$vuetify.maira_questionnaire.introduction_message')"
                            :placeholder="$vuetify.lang.t('$vuetify.profile.introduction_msg_txt')"
                            outlined
                            rows="3"
                          ></v-textarea>
                          <v-tooltip bottom max-width="350">
                              <template v-slot:activator="{ on }">
                                  <span v-on="on" class="info-icon">i</span>
                              </template>
                              <span>{{ $vuetify.lang.t("$vuetify.maira_questionnaire.intro_tooltip") }}</span>
                          </v-tooltip>
                        </div>
                      </v-col>

                      <!-- Completion Message -->
                      <v-col cols="12">
                        <div class="textarea-with-tooltip">
                          <v-textarea
                            v-model="config.completion_message"
                            :label="$vuetify.lang.t('$vuetify.maira_questionnaire.completion_message')"
                            :placeholder="$vuetify.lang.t('$vuetify.profile.completion_msg_txt')"
                            outlined
                            rows="3"
                          ></v-textarea>
                          <v-tooltip bottom max-width="350">
                            <template v-slot:activator="{ on }">
                              <span v-on="on" class="info-icon">i</span>
                            </template>
                            <span>{{ $vuetify.lang.t("$vuetify.maira_questionnaire.completion_message_tooltip") }}</span>
                          </v-tooltip>
                        </div>
                      </v-col>

                      <!-- Frequency  Configuration -->
                      <v-col cols="12">
                        <v-card outlined>
                          <v-card-subtitle class="py-2">
                            <span class="text-subtitle-2">{{ $vuetify.lang.t("$vuetify.maira_questionnaire.frequency_settings") }}</span>
                          </v-card-subtitle>
                          <v-card-text class="pt-0">
                            <v-row>
                              <v-col cols="12" md="6">
                                <v-text-field
                                  type="number"
                                  v-model.number="config.frequency.max_attempts"
                                  :rules="maxAttemptsRules"
                                  :label="$vuetify.lang.t('$vuetify.maira_questionnaire.max_attempts')"
                                  :placeholder="$vuetify.lang.t('$vuetify.profile.max_attempts_hint_txt')"
                                  outlined
                                  min="1"
                                >
                                <template v-slot:append>
                                  <v-tooltip bottom max-width="350">
                                    <template v-slot:activator="{ on }">
                                      <span v-on="on" class="info-icon">i</span>
                                    </template>
                                    <span>{{ $vuetify.lang.t("$vuetify.maira_questionnaire.max_attempts_tooltip") }}</span>
                                  </v-tooltip>
                                </template>
                                </v-text-field>
                              </v-col>
                              <v-col cols="12" md="6">
                                <v-text-field
                                  type="number"
                                  v-model.number="config.frequency.retry_interval_days"
                                  :rules="retryIntervalRules(config)"
                                  :label="$vuetify.lang.t('$vuetify.maira_questionnaire.retry_interval')"
                                  :placeholder="$vuetify.lang.t('$vuetify.profile.retry_interval_placeholder')"
                                  outlined
                                  min="0"
                                >
                                  <template v-slot:append>
                                    <v-tooltip bottom max-width="350">
                                      <template v-slot:activator="{ on }">
                                        <span v-on="on" class="info-icon">i</span>
                                      </template>
                                      <span>{{ $vuetify.lang.t("$vuetify.maira_questionnaire.retry_interval_tooltips") }}</span>
                                    </v-tooltip>
                                  </template>
                                </v-text-field>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </template>
          <template v-else>
            <v-card outlined class="pa-4">
              <p>{{ $vuetify.lang.t("$vuetify.profile.empty_questionnaire_config") }}</p>
            </v-card>
          </template>
          <DialogModal
            v-model="deleteDialog"
            :title="$vuetify.lang.t('$vuetify.common.confirm_alert_title_txt')"
            :message="$vuetify.lang.t('$vuetify.common.confirm_alert_msg_txt_10')"
            :confirmButtonText="$vuetify.lang.t('$vuetify.common.confirm_btn')"
            :cancelButtonText="$vuetify.lang.t('$vuetify.common.cancel_btn')"
            @onConfirmation="deleteConfigConfirmed"
            @onCancel="deleteDialog = false"
          />
        </div>
      </div>

      <div class="content-moderation-section">
        <v-row>
          <v-col cols="12">
            <v-divider class="my-4"></v-divider>
            <v-row align="center" class="mb-4 mt-0">
              <v-col cols="12" sm="auto" class="d-flex align-center py-1">
                <p class="text-h6 mb-0">
                  {{ $vuetify.lang.t('$vuetify.gpt_profile.content_moderation.title') }}
                </p>

                <v-tooltip top max-width="350">
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-on="on"
                      v-bind="attrs"
                      class="info-icon ml-2"
                    >i</span>
                  </template>
                  <span>
                    {{ $vuetify.lang.t('$vuetify.gpt_profile.content_moderation.tooltip_text') }}
                  </span>
                </v-tooltip>
              </v-col>

              <v-col cols="12" sm="" class="d-flex justify-sm-end py-1">
                <v-btn color="primary" @click="addModerationConfig">
                  <v-icon small left>mdi-plus</v-icon>
                  {{ $vuetify.lang.t('$vuetify.gpt_profile.content_moderation.add_category_btn') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row class="mb-4">
          <v-col cols="12">
            <div class="d-flex align-center">
              <v-checkbox
                v-model="profile.content_moderation.is_filtering_enabled"
                :label="$vuetify.lang.t('$vuetify.gpt_profile.content_moderation.filtering_enabled_label')"
                color="primary"
                class="ma-0 pa-0 flex-grow-0 no-hover"
                :ripple="false"
              ></v-checkbox>

              <v-tooltip top max-width="350">
                <template v-slot:activator="{ on, attrs }">
                  <span
                    v-on="on"
                    v-bind="attrs"
                    class="info-icon mb-5"
                  >
                  i
                  </span>
                </template>
                <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.content_moderation.filtering_enabled_tooltip') }}</span>
              </v-tooltip>
            </div>
          </v-col>
        </v-row>

        <div>
          <template v-if="profile.content_moderation.categories && profile.content_moderation.categories.length">
            <v-expansion-panels v-model="expandedModerationPanel">
              <v-expansion-panel
                v-for="(cfg, idx) in profile.content_moderation.categories"
                :key="`cm-${idx}`"
              >
                <v-expansion-panel-header>
                  <div class="d-flex align-center justify-space-between w-100">
                    <span class="text-subtitle-1">{{ moderationPanelTitle(cfg, idx) }}</span>
                    <v-spacer />
                    <v-btn icon small color="error" @click.stop="confirmModerationDelete(idx)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="field-row">
                        <v-combobox
                          v-model="cfg.name"
                          :items="fetchedCategoryOptions"
                          @input="cfg.name = (typeof $event === 'string' ? $event.toLowerCase() : '')"
                          @change="onCategoryNameChange(idx, $event)"
                          @focus="onCategoryNameFocus(idx)"
                          @blur="onCategoryNameBlur(idx)"
                          @keydown.enter="triggerManualBlur(idx, $event)"
                          :label="$vuetify.lang.t('$vuetify.gpt_profile.content_moderation.name_label')"
                          :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.content_moderation.name_placeholder')"
                          outlined
                          clearable
                          :rules="moderationNameRules(idx)"
                        >
                          <template v-slot:append>
                            <v-tooltip bottom max-width="350">
                              <template v-slot:activator="{ on }">
                                <span v-on="on" class="info-icon">i</span>
                              </template>
                              <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.content_moderation.name_tooltip_txt') }}</span>
                            </v-tooltip>
                          </template>
                        </v-combobox>
                      </div>
                    </v-col>

                    <v-col cols="12" md="6">
                      <div class="field-row">
                        <v-combobox
                          v-model="cfg.keywords"
                          :items="[]"
                          :label="$vuetify.lang.t('$vuetify.gpt_profile.content_moderation.keywords_label')"
                          :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.content_moderation.keywords_placeholder')"
                          outlined
                          multiple
                          chips
                          small-chips
                          clearable
                          @change="onContentModerationKeywordsChange(idx, $event)"
                        >
                          <template v-slot:selection="{ attrs, item, select, selected }">
                            <v-chip
                              v-bind="attrs"
                              @click="select"
                              @click:close="removeContentModerationKeyword(idx, item)"
                              :model-value="selected"
                              close
                              small
                            >
                              <span>{{ item }}</span>
                            </v-chip>
                          </template>

                          <template v-slot:append>
                            <v-tooltip bottom max-width="350">
                              <template v-slot:activator="{ on }">
                                <span v-on="on" class="info-icon">i</span>
                              </template>
                              <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.content_moderation.keywords_tooltip_txt') }}</span>
                            </v-tooltip>
                          </template>
                        </v-combobox>
                      </div>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <div class="field-row">
                        <v-combobox
                          v-model="cfg.detection_rules"
                          :items="[]"
                          :label="$vuetify.lang.t('$vuetify.gpt_profile.content_moderation.detection_rules_label')"
                          :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.content_moderation.detection_rules_placeholder')"
                          outlined
                          multiple
                          chips
                          small-chips
                          clearable
                          @change="onDetectionRulesChange(idx, $event)"
                        >
                          <template v-slot:selection="{ attrs, item, select, selected }">
                            <v-chip
                              v-bind="attrs"
                              @click="select"
                              @click:close="removeDetectionRule(idx, item)"
                              :model-value="selected"
                              close
                              small
                            >
                              <span>{{ item }}</span>
                            </v-chip>
                          </template>

                          <template v-slot:append>
                            <v-tooltip bottom max-width="350">
                              <template v-slot:activator="{ on }">
                                <span v-on="on" class="info-icon">i</span>
                              </template>
                              <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.content_moderation.detection_rules_tooltip_txt') }}</span>
                            </v-tooltip>
                          </template>
                        </v-combobox>
                      </div>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <div class="d-flex align-center mb-2">
                        <span class="text-subtitle-1">
                          {{ $vuetify.lang.t('$vuetify.gpt_profile.content_moderation.severity_actions_title') }}
                        </span>
                        <v-tooltip bottom max-width="350">
                          <template v-slot:activator="{ on }">
                            <span 
                              v-on="on" 
                              class="info-icon" 
                            >
                              i
                            </span>
                          </template>
                          <span>
                            {{ $vuetify.lang.t('$vuetify.gpt_profile.content_moderation.severity_actions_tooltip_txt') }}
                          </span>
                        </v-tooltip>
                      </div>
                      <div class="severity-row">
                        <div class="sev-cell">
                          <div class="sev-label">{{ $vuetify.lang.t('$vuetify.gpt_profile.content_moderation.severity_low_label') }}</div>
                          <v-select
                            v-model="cfg.severity_actions.low"
                            :items="actionOptions"
                            item-text="text"
                            item-value="value"
                            outlined
                            hide-details
                          />
                        </div>

                        <div class="sev-cell">
                          <div class="sev-label">{{ $vuetify.lang.t('$vuetify.gpt_profile.content_moderation.severity_medium_label') }}</div>
                          <v-select
                            v-model="cfg.severity_actions.medium"
                            :items="actionOptions"
                            item-text="text"
                            item-value="value"
                            outlined
                            hide-details
                          />
                        </div>

                        <div class="sev-cell">
                          <div class="sev-label">{{ $vuetify.lang.t('$vuetify.gpt_profile.content_moderation.severity_high_label') }}</div>
                          <v-select
                            v-model="cfg.severity_actions.high"
                            :items="actionOptions"
                            item-text="text"
                            item-value="value"
                            outlined
                            hide-details
                          />
                        </div>
                      </div>
                    </v-col>
                  </v-row>

                  <v-row v-if="hasTerminationAction(cfg)">
                    <v-col cols="12">
                      <div class="textarea-with-tooltip">
                        <v-textarea
                          v-model="cfg.termination_message"
                          outlined
                          rows="3"
                          :label="$vuetify.lang.t('$vuetify.gpt_profile.content_moderation.termination_message_label')"
                          :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.content_moderation.termination_message_placeholder')"
                          @blur="sanitizeTerminationMessage(cfg)"
                        ></v-textarea>
                        <v-tooltip bottom max-width="350">
                          <template #activator="{ on }">
                            <span class="info-icon" v-on="on">i</span>
                          </template>
                          <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.content_moderation.termination_message_tooltip_txt') }}</span>
                        </v-tooltip>
                      </div>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </template>

          <template v-else>
            <v-card outlined class="pa-4">
              <p>{{ $vuetify.lang.t('$vuetify.gpt_profile.content_moderation.no_categories_msg') }}</p>
            </v-card>
          </template>

          <DialogModal
            v-model="deleteModerationDialog"
            :title="$vuetify.lang.t('$vuetify.common.confirm_alert_title_txt')"
            :message="$vuetify.lang.t('$vuetify.gpt_profile.content_moderation.confirm_delete_category')"
            :confirmButtonText="$vuetify.lang.t('$vuetify.common.confirm_btn')"
            :cancelButtonText="$vuetify.lang.t('$vuetify.common.cancel_btn')"
            @onConfirmation="deleteModerationConfirmed"
            @onCancel="deleteModerationDialog = false"
          />
          <DialogModal
            v-model="deleteModerationBlockedDialog"
            :title="$vuetify.lang.t('$vuetify.gpt_profile.content_moderation.cannot_delete_title')"
            :message="moderationDeleteMessage"
            :confirmButtonText="'OK'"
            :cancelButtonText="null"
            @onConfirmation="deleteModerationBlockedDialog = false"
          />
        </div>
      </div>

      <div class="external-config">
        <v-row>
          <v-col cols="12">
            <v-divider class="my-4"></v-divider>
            <div class="d-flex flex-column mb-4">
              <v-row align="center" class="mb-0 mt-0">
                <v-col cols="12" sm="auto" class="py-1">
                  <div class="text-h6" style="display: block; line-height: 1.5;">
                    {{ $vuetify.lang.t('$vuetify.gpt_profile.external_system_config.title') }}
                    <v-tooltip top max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          v-on="on"
                          v-bind="attrs"
                          class="info-icon"
                          style="display: inline-block; vertical-align: middle; margin-left: 6px;"
                        >i</span>
                      </template>
                      <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.external_system_config.tooltip_text') }}</span>
                    </v-tooltip>
                  </div>
                </v-col>

                <v-col cols="12" sm="" class="d-flex justify-sm-end py-1">
                  <v-btn color="primary" @click="addExternalConfig">
                    <v-icon small left>mdi-plus</v-icon>
                    {{ $vuetify.lang.t('$vuetify.gpt_profile.external_system_config.add_external_config_btn') }}
                  </v-btn>
                </v-col>
              </v-row>

              <div class="info-notice mt-2 pl-1 d-flex align-center flex-wrap">
                <v-icon small color="info" class="mr-1">mdi-information</v-icon>
                <span class="info-notice-text">{{ $vuetify.lang.t('$vuetify.gpt_profile.external_system_config.generic_config_notice') }}</span>
              </div>
            </div>
          </v-col>
        </v-row>

        <div>
          <template v-if="profile.external_system_configs && profile.external_system_configs.length">
            <v-expansion-panels v-model="expandedExternalPanel">
              <v-expansion-panel
                v-for="(cfg, idx) in profile.external_system_configs"
                :key="`ext-${idx}`"
              >
                <v-expansion-panel-header>
                  <div class="d-flex align-center justify-space-between w-100">
                    <span class="text-subtitle-1">{{ externalPanelTitle(cfg, idx) }}</span>
                    <v-spacer />
                    <v-btn icon small color="error" @click.stop="confirmExternalDelete(idx)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                  <v-row>
                    <v-col cols="12" md="12">
                      <v-text-field
                        v-model="cfg.name"
                        :label="$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.config_name_label')"
                        :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.config_name_placeholder')"
                        outlined
                        clearable
                        :rules="externalConfigNameRules(idx)"
                      >
                        <template v-slot:append>
                          <v-tooltip bottom max-width="350">
                            <template v-slot:activator="{ on }">
                              <span v-on="on" class="info-icon">i</span>
                            </template>
                            <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.external_system_config.config_name_tooltip_txt') }}</span>
                          </v-tooltip>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <div class="textarea-with-tooltip">  
                        <v-textarea
                          v-model="cfg.redirection_message"
                          @blur="sanitizeRedirectionMessage(cfg)"
                          :label="$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.redirection_message_label')"
                          :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.redirection_message_placeholder')"
                          outlined
                          rows="3"
                        ></v-textarea>
                        <v-tooltip bottom max-width="350">
                          <template v-slot:activator="{ on }">
                            <span v-on="on" class="info-icon">i</span>
                          </template>
                          <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.external_system_config.redirection_message_tooltip_txt') }}</span>
                        </v-tooltip>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="cfg.redirection_url"
                        :label="$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.redirection_url_label')"
                        :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.redirection_url_placeholder')"
                        outlined
                        clearable
                        :rules="externalRedirectUrlRules(idx)"
                      >
                        <template v-slot:append>
                          <v-tooltip bottom max-width="350">
                            <template v-slot:activator="{ on }">
                              <span v-on="on" class="info-icon">i</span>
                            </template>
                            <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.external_system_config.redirection_url_tooltip_txt') }}</span>
                          </v-tooltip>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model.number="cfg.redirection_delay_seconds"
                        type="number"
                        :rules="redirectDelayRules()"
                        min="1"
                        :label="$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.redirection_delay_label')"
                        :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.redirection_delay_placeholder')"
                        outlined
                        clearable
                      >
                        <template v-slot:append>
                          <v-tooltip bottom max-width="350">
                            <template v-slot:activator="{ on }">
                              <span v-on="on" class="info-icon">i</span>
                            </template>
                            <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.external_system_config.redirection_delay_tooltip_txt') }}</span>
                          </v-tooltip>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4" class="d-flex justify-center align-center">
                      <div>
                        <v-row class="align-center ms-1 py-1">
                          <v-checkbox
                            v-model="cfg.auto_redirect"
                            :label="$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.auto_redirect_label')"
                          ></v-checkbox>
                          <v-tooltip bottom max-width="450">
                            <template v-slot:activator="{ on, attrs }">
                              <span
                                v-on="on"
                                v-bind="attrs"
                                class="info-icon d-flex align-center justify-center ml-4 mb-4"
                              >
                                i
                              </span>
                            </template>
                          
                            <span>
                              {{ $vuetify.lang.t('$vuetify.gpt_profile.external_system_config.auto_redirect_tooltip_txt') }}
                            </span>
                          </v-tooltip>
                        </v-row>
                      </div>
                    </v-col>
                  </v-row>

                  <v-row>
                     <v-col cols="12" md="12">
                      <v-select
                        class="shareable-info-select"
                        v-model="cfg.shareable_info"
                        :items="fetchedShareableFieldOptions"
                        :label="$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.shareable_info_label')"
                        :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.shareable_info_placeholder')"
                        outlined
                        multiple
                        chips
                        deletable-chips
                        clearable
                        :loading="isLoadingShareable"
                        @change="onShareableInfoChange(idx, $event)"
                        :rules="externalShareableRules(idx)"
                      >
                        <template v-slot:append>
                          <v-tooltip bottom max-width="350">
                            <template v-slot:activator="{ on }">
                              <div 
                                v-on="on" 
                                class="info-icon-wrapper"
                              >
                                <span class="info-icon">i</span>
                              </div>
                            </template>
                            <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.external_system_config.shareable_info_tooltip_txt') }}</span>
                          </v-tooltip>
                        </template>
                      </v-select>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <v-select
                        class="redirect-categories-select"
                        :ref="`externalRedirect_${idx}`"
                        v-model="cfg.redirect_triggering_categories"
                        :items="availableCategoryOptionsFor(idx)"
                        :label="$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.redirect_triggering_categories_label')"
                        :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.redirect_triggering_categories_placeholder')"
                        outlined
                        multiple
                        chips
                        deletable-chips
                        clearable
                        :loading="isLoadingCategories"
                        @change="onRedirectCategoriesChange(idx, $event)"
                        :rules="externalRedirectCategoriesRules(idx)"
                        :error-messages="cfg._redirectError ? [cfg._redirectError] : []"
                      >
                        <template v-slot:append>
                          <v-tooltip bottom max-width="350">
                            <template v-slot:activator="{ on }">
                               <div 
                                v-on="on" 
                                class="info-icon-wrapper"
                              >
                                <span class="info-icon">i</span>
                              </div>
                            </template>
                            <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.external_system_config.redirect_triggering_categories_tooltip_txt') }}</span>
                          </v-tooltip>
                        </template>
                      </v-select>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </template>

          <template v-else>
            <v-card outlined class="pa-4">
              <p>{{ $vuetify.lang.t('$vuetify.gpt_profile.external_system_config.no_external_configs_msg') }}</p>
            </v-card>
          </template>

          <v-card class="mt-4 profile-liff-config" v-if="showLiffConfig">
            <v-card-title class="headline">
              <div class="text-h6" style="display: block; line-height: 1.5;">
                {{ $vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.config_title') }}<v-tooltip top max-width="350">
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-on="on"
                      v-bind="attrs"
                      class="info-icon"
                      style="display: inline-block; vertical-align: middle; margin-left: 6px;"
                    >i</span>
                  </template>
                  <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.config_tooltip') }}</span>
                </v-tooltip>
              </div>
            </v-card-title>
            
            <v-card-text>
              <v-alert
                v-if="!isLiffEnabled"
                type="warning"
                outlined
                dense
                class="mb-4"
                icon="mdi-alert"
              >
                <div class="text-body-2">
                  {{ liffDisabledMessage }}
                </div>
              </v-alert>

              <v-row v-if="isLiffEnabled">
                <v-col cols="12">
                  <v-text-field
                    v-model="profile.liff_endpoint_url_config.liff_id"
                    :label="$vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.liff_id_label')"
                    outlined
                    :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.liff_id_placeholder')"
                    :hint="$vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.liff_id_hint')"
                    persistent-hint
                    clearable
                    :disabled="!isLiffEnabled"
                    :rules="isLiffIdRequired ? [required($vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.liff_id_label'))] : []"
                  >
                    <template v-slot:append>
                      <v-tooltip bottom max-width="350">
                        <template v-slot:activator="{ on }">
                          <div v-on="on">
                            <span class="info-icon">i</span>
                          </div>
                        </template>
                        <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.liff_id_tooltip') }}</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>

              <v-row v-if="isLiffEnabled">
                <v-col cols="12">
                  <v-autocomplete
                    v-model="profile.liff_endpoint_url_config.group_invitation_token"
                    :items="groupInvitationOptions"
                    :loading="isCreatingInvitation"
                    :label="$vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.group_invitaiton_label')"
                    outlined
                    item-text="displayLabel"
                    item-value="jti"
                    :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.select_group_invitation_placeholer')"
                    clearable
                    :hint="$vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.choose_active_group_invitation_hint')"
                    persistent-hint
                    :disabled="!isLiffEnabled"
                    :rules="isGroupInvitationRequired ? [required($vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.group_invitaiton_label'))] : []"
                  >
                    <template v-slot:prepend-item>
                      <v-list-item @click="createAndSelectInvitation">
                        <v-list-item-icon>
                          <v-icon color="primary">mdi-plus-circle-outline</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="primary--text font-weight-medium">
                            {{ $vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.create_new_invitation') }}
                          </v-list-item-title>
                          <v-list-item-subtitle class="text-caption">
                            {{ $vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.create_new_invitation_hint') }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider class="mt-2"></v-divider>
                    </template>

                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title class="d-flex align-center">
                          <span class="mr-2 font-weight-medium">
                            {{ item.title || $vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.no_title') }}
                          </span>
                          <span class="text-caption text--secondary">
                            : {{ item.jti }}
                          </span>
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>

                    <template v-slot:selection="{ item }">
                      <span>{{ item.title || item.jti }}</span>
                    </template>
                    <template v-slot:append>
                      <v-tooltip bottom max-width="350">
                        <template v-slot:activator="{ on }">
                          <div v-on="on">
                            <span class="info-icon">i</span>
                          </div>
                        </template>
                        <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.group_invitation_tooltip') }}</span>
                      </v-tooltip>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>

              <v-row v-if="isLiffEnabled">
                <v-col cols="12">
                  <v-combobox
                    class="profile-access-tags-combobox"
                    v-model="profile.selected_profile_tags"
                    :label="$vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.select_profile_access_tags_label')"
                    variant="solo"
                    chips
                    clearable
                    outlined
                    :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.select_profile_access_tags_label')"
                    :hint="$vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.choose_tags_hint')"
                    persistent-hint
                    multiple
                    :disabled="!isLiffEnabled"
                    :class="{ 'no-dropdown-icon': !showProfileTags }"
                  >
                    <template v-slot:no-data>
                      <div v-if="showProfileTags && profileTagsOptions.length > 0" class="px-4 py-2">
                        <div class="d-flex mb-3 pb-2 header-row">
                          <div class="profile-name-column text-subtitle-1 font-weight-bold">
                            {{ $vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.tags_header_first') }}
                          </div>
                          <div class="tags-column text-subtitle-1 font-weight-bold">
                            {{ $vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.tags_header_second') }}
                          </div>
                        </div>
                        
                        <div 
                          v-for="profileOption in profileTagsOptions" 
                          :key="profileOption.profileId" 
                          class="d-flex mb-3 pb-2 data-row"
                          :class="{ 'current-profile-row': profileOption.isCurrent }"
                        >
                          <div class="profile-name-column">
                            <v-tooltip bottom v-if="isProfileNameTruncated(profileOption.label)">
                              <template v-slot:activator="{ on, attrs }">
                                <div 
                                  class="text-subtitle-2 font-weight-bold profile-name-text"
                                  v-bind="attrs"
                                  v-on="on"
                                >
                                  {{ truncateProfileName(profileOption.label) }}:
                                </div>
                              </template>
                              <span class="tooltip-text">{{ profileOption.label }}</span>
                            </v-tooltip>
                            
                            <div 
                              v-else
                              class="text-subtitle-2 font-weight-bold profile-name-text"
                            >
                              {{ truncateProfileName(profileOption.label) }}:
                            </div>
                          </div>
                          
                          <div class="tags-column">
                            <div class="tag-badges">
                              <v-chip
                                v-for="tag in profileOption.tags"
                                :key="`${profileOption.profileId}-${tag}`"
                                class="mr-1 mb-1 clickable"
                                small
                                :color="tagSelected(tag) ? 'primary' : 'default'"
                                @click.stop="ProfileToggleTag(tag)"
                                clickable
                              >
                                {{ tag }}
                              </v-chip>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div v-else-if="!isLoadingProfiles" class="px-4 py-3 text-center grey--text">
                        {{ $vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.no_access_tags') }}
                      </div>
                    </template>

                    <template v-slot:selection="{ item }">
                      <v-chip
                        close
                        @click:close="removeProfileTag(item)"
                      >
                        {{ item }}
                      </v-chip>
                    </template>

                    <template v-slot:append>
                      <v-tooltip bottom max-width="350">
                        <template v-slot:activator="{ on }">
                          <div v-on="on" class="info-icon-wrapper">
                            <span class="info-icon">i</span>
                          </div>
                        </template>
                        <span>
                          {{ $vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.select_profile_access_tags_tooltip') }}
                        </span>
                      </v-tooltip>
                    </template>
                  </v-combobox>
                </v-col>
              </v-row>

              <v-row v-if="originalLiffUrl && isLiffEnabled">
                <v-col cols="12">
                  <v-textarea
                    v-model="displayedLiffUrl"
                    :label="$vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.generated_url_label')"
                    outlined
                    readonly
                    auto-grow
                    rows="1"
                    :hint="$vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.auto_generate_hint')"
                    persistent-hint
                    :disabled="!isLiffEnabled"
                  >
                    <template v-slot:append>
                      <v-tooltip bottom max-width="200">
                        <template v-slot:activator="{ on }">
                          <v-btn
                            text
                            small
                            color="primary"
                            v-on="on"
                            @click="togglePreview"
                            class="mr-2"
                          >
                            <v-icon>
                              {{ isPreviewMode ? 'mdi-undo' : 'mdi-eye' }}
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>
                          {{ isPreviewMode
                            ? $vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.undo_hint')
                            : $vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.preview_hint')
                          }}
                        </span>
                      </v-tooltip>
                      
                      <v-tooltip bottom max-width="200">
                        <template v-slot:activator="{ on }">
                          <v-btn
                            icon
                            small
                            v-on="on"
                            @click="copyLiffUrl"
                          >
                            <v-icon small>mdi-content-copy</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.copy_url') }}</span>
                      </v-tooltip>
                    </template>
                  </v-textarea>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <DialogModal
            v-model="deleteExternalDialog"
            :title="$vuetify.lang.t('$vuetify.common.confirm_alert_title_txt')"
            :message="$vuetify.lang.t('$vuetify.common.confirm_alert_msg_txt_10')"
            :confirmButtonText="$vuetify.lang.t('$vuetify.common.confirm_btn')"
            :cancelButtonText="$vuetify.lang.t('$vuetify.common.cancel_btn')"
            @onConfirmation="deleteExternalConfirmed"
            @onCancel="deleteExternalDialog = false"
          />
        </div>
      </div>

      <div class="response-filters-section">
        <v-row>
          <v-col cols="12">
            <v-divider class="my-4"></v-divider>
            <v-row align="center" class="mb-4 mt-0">
              <v-col cols="12" sm="auto" class="d-flex align-center py-1">
                <p class="text-h6 mb-0">
                  {{ $vuetify.lang.t('$vuetify.gpt_profile.response_filters.title') }}
                </p>

                <v-tooltip top max-width="350">
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-on="on"
                      v-bind="attrs"
                      class="info-icon ml-2"
                    >i</span>
                  </template>
                  <span>
                    {{ $vuetify.lang.t('$vuetify.gpt_profile.response_filters.tooltip_text') }}
                  </span>
                </v-tooltip>
              </v-col>

              <v-col cols="12" sm="" class="d-flex justify-sm-end py-1">
                <v-btn color="primary" @click="addResponseFilter">
                  <v-icon small left>mdi-plus</v-icon>
                  {{ $vuetify.lang.t('$vuetify.gpt_profile.response_filters.add_filter_btn') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <div>
          <template v-if="profile.response_filters && profile.response_filters.length">
            <v-expansion-panels v-model="expandedResponseFilterPanel">
              <v-expansion-panel
                v-for="(filter, idx) in profile.response_filters"
                :key="`response-filter-${filter._tmpId || idx}`"
              >
                <v-expansion-panel-header>
                  <div class="d-flex align-center w-100 response-filter-header">
                    <v-tooltip bottom max-width="400" v-if="truncatedFilterTitles[`filter-${idx}`]">
                      <template v-slot:activator="{ on, attrs }">
                        <span 
                          ref="filterTitleRef"
                          v-bind="attrs"
                          v-on="on"
                          class="text-subtitle-1 text-truncate response-filter-title"
                          @mouseenter="checkTextTruncate($event, filter, idx)"
                        >
                          {{ responseFilterPanelTitle(filter, idx) }}
                        </span>
                      </template>
                      <span>{{ responseFilterPanelTitle(filter, idx) }}</span>
                    </v-tooltip>
                    <span 
                      v-else
                      ref="filterTitleRef"
                      class="text-subtitle-1 text-truncate response-filter-title"
                      @mouseenter="checkTextTruncate($event, filter, idx)"
                    >
                      {{ responseFilterPanelTitle(filter, idx) }}
                    </span>
                    <v-chip x-small label class="ml-3 flex-shrink-0" :color="filter.type === 'regex' ? 'deep-purple lighten-4' : 'blue-grey lighten-4'">
                      {{ responseFilterTypeLabel(filter.type) }}
                    </v-chip>
                    <v-chip
                      v-if="filter.is_case_sensitive"
                      x-small
                      label
                      class="ml-2 flex-shrink-0"
                      color="amber lighten-4"
                    >
                      {{ $vuetify.lang.t('$vuetify.gpt_profile.response_filters.case_sensitive_chip') }}
                    </v-chip>
                    <v-spacer />
                    <v-btn icon small color="error" @click.stop="confirmResponseFilterDelete(idx)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                  <v-row no-gutters>
                    <v-col cols="12" md="8">
                      <div class="field-row">
                        <v-text-field
                          :ref="`responseFilterPattern_${idx}`"
                          v-model="filter.pattern"
                          :label="$vuetify.lang.t('$vuetify.gpt_profile.response_filters.pattern_label')"
                          :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.response_filters.pattern_placeholder')"
                          outlined
                          clearable
                          :rules="responseFilterPatternRules(idx)"
                        >
                          <template v-slot:append>
                            <v-tooltip bottom max-width="350">
                              <template v-slot:activator="{ on }">
                                <span v-on="on" class="info-icon">i</span>
                              </template>
                              <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.response_filters.pattern_tooltip_txt') }}</span>
                            </v-tooltip>
                          </template>
                        </v-text-field>
                      </div>
                    </v-col>

                    <v-col cols="12" md="4" class="pl-md-4">
                      <div class="response-filter-control-box response-filter-type-box">
                        <div class="d-flex align-center mb-1">
                          <span class="text-body-2 text--secondary">
                            {{ $vuetify.lang.t('$vuetify.gpt_profile.response_filters.type_label') }}
                          </span>
                          <v-tooltip top max-width="350">
                            <template v-slot:activator="{ on, attrs }">
                              <span v-on="on" v-bind="attrs" class="info-icon ml-2">i</span>
                            </template>
                            <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.response_filters.type_tooltip') }}</span>
                          </v-tooltip>
                        </div>
                        <v-btn-toggle
                          v-model="filter.type"
                          mandatory
                          dense
                          color="primary"
                          class="response-filter-type-toggle"
                        >
                          <v-btn
                            v-for="option in responseFilterTypeOptions"
                            :key="option.value"
                            :value="option.value"
                            small
                          >
                            {{ option.text }}
                          </v-btn>
                        </v-btn-toggle>
                      </div>
                    </v-col>
                  </v-row>

                  <v-row no-gutters>
                    <v-col cols="12" md="8">
                      <div class="field-row">
                        <v-text-field
                          v-model="filter.replace_with"
                          :label="$vuetify.lang.t('$vuetify.gpt_profile.response_filters.replace_with_label')"
                          :placeholder="$vuetify.lang.t('$vuetify.gpt_profile.response_filters.replace_with_placeholder')"
                          :hint="$vuetify.lang.t('$vuetify.gpt_profile.response_filters.replace_with_hint')"
                          persistent-hint
                          outlined
                          clearable
                        />
                      </div>
                    </v-col>

                    <v-col cols="12" md="4" class="pl-md-4">
                      <div class="response-filter-control-box">
                        <v-checkbox
                          v-model="filter.is_case_sensitive"
                          :label="$vuetify.lang.t('$vuetify.gpt_profile.response_filters.case_sensitive_label')"
                          color="primary"
                          hide-details
                          class="mt-0 pt-0"
                        ></v-checkbox>

                        <v-tooltip top max-width="350">
                          <template v-slot:activator="{ on, attrs }">
                            <span v-on="on" v-bind="attrs" class="info-icon ml-2">i</span>
                          </template>
                          <span>{{ $vuetify.lang.t('$vuetify.gpt_profile.response_filters.case_sensitive_tooltip') }}</span>
                        </v-tooltip>
                      </div>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </template>
          <template v-else>
            <v-card outlined class="pa-4">
              <p>{{ $vuetify.lang.t('$vuetify.gpt_profile.response_filters.no_filters_msg') }}</p>
            </v-card>
          </template>

          <DialogModal
            v-model="deleteResponseFilterDialog"
            :title="$vuetify.lang.t('$vuetify.common.confirm_alert_title_txt')"
            :message="$vuetify.lang.t('$vuetify.common.confirm_alert_msg_txt_10')"
            :confirmButtonText="$vuetify.lang.t('$vuetify.common.confirm_btn')"
            :cancelButtonText="$vuetify.lang.t('$vuetify.common.cancel_btn')"
            @onConfirmation="deleteResponseFilterConfirmed"
            @onCancel="deleteResponseFilterDialog = false"
          />
        </div>
      </div>
    </template>
  </v-form>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { 
  UPDATE_GPT_PROFILE_STATE, 
  GET_GPT_MODEL_DETAILS, 
  MAIRA_QUESTIONNAIRE,
  FETCH_SHAREABLE_DATA,
  GET_DEFAULT_CATEGORIES,
  GET_PUBLIC_INVITATIONS_LIST,
  GET_GPT_PROFILES,
  UPDATE_PUBLIC_INVITATION_URL,
  GENERATE_PUBLIC_INVITATION_URL,
  ADD_ALERT,
} from "@/store/_actiontypes";
import {
  PROMPT_TEMPLATE_SEPARATORS_TOKEN_COUNT,
  CONTAMINATION_CONTEXTS,
  MAX_DATASET_NAME_LENGTH,
  RESPONSE_FILTER_TYPES
} from "@/global/_constants";
import getEnv from "@/config/env";
import { getTimezonePickerItems } from "@/helpers/timezone";
import { GptProfileMapper } from "@/helpers/dataMappers/gptProfile";
import DialogModal from "@/components/common/DialogModal";
import validations from "@/helpers/validations";
import { debounce } from "lodash";

const MIN_CODE_EXECUTION_OUTPUT_TOKENS = 16000;

export default {
  components: { DialogModal },
  props: {
    showtags: {
      type: Boolean,
      default: true
    },
    showConfig: {
      type: Boolean,
      default: true
    },
    showLiffConfig: {
      type: Boolean,
      default: true
    },
  },
  data() {
    return {
      modelFieldError: "",
      modelDetailsStatus: "idle",
      modelDetailsKey: null,
      truncatedFilterTitles: {},
      penaltyRules: [
        value => value <= 2 || this.$vuetify.lang.t('$vuetify.gpt_profile.max_2'),
        value => value >= 0 || this.$vuetify.lang.t('$vuetify.gpt_profile.min_0'),
      ],
      modelChangeToken: 0,
      isManualModelChange: false,
      // The user's full desired tool set, independent of what the current model supports. applySupportedTools() derives the visible profile.tools from this + current model capabilities;
      // onToolsChange() is the only place that updates it from genuine user input. null means "not seeded yet" - seeded lazily from profile.tools on first use.
      rememberedTools: null,
      temperatureMeta: { min: 0, max: 2, default: 0, required: false },
      topPMeta: { min: 0, max: 1, default: 1, required: false },
      topKRules: [
        value => value <= 50 || this.$vuetify.lang.t('$vuetify.gpt_profile.max_50'),
        value => value >= 1 || this.$vuetify.lang.t('$vuetify.gpt_profile.min_1')
      ],
      completionTokenRules: [],
      profileLoader: true,
      maxToken: 32000,
      localProfile: this.profile,
      searchTokenRules: [
        value => value <= this.maxToken || `${this.$vuetify.lang.t('$vuetify.gpt_profile.max_dynamic')}${this.maxToken}`,
        value => value >= 0 || this.$vuetify.lang.t('$vuetify.gpt_profile.min_0'),
      ],
      chatHistoryLengthRules: [
        value => value >= 0 || this.$vuetify.lang.t('$vuetify.gpt_profile.min_zero_or_greater'),
        value => Number.isInteger(Number(value)) || this.$vuetify.lang.t('$vuetify.gpt_profile.integer_required'),
      ],
      personalizerOnlyItems: [
        { text: "true", value: true },
        { text: "false", value: false },
      ],
      isAutoEvaluation: [
        { text: "true", value: true },
        { text: "false", value: false },
      ],
      search_mode: [
        { text: "context", value: "context" },
        { text: "full_text", value: "full_text" },
        { text: "hybrid", value: "hybrid" },
      ],
      active: [
        { text: "true", value: true },
        { text: "false", value: false },
      ],
      use_intelligent_detection: [
        { text: "true", value: true },
        { text: "false", value: false },
      ],
      gptRandomnessOptions: [
        {
          value: "accurate_ans",
          name: "Generate answer only from given data",
        },
        {
          value: "generalized_ans",
          name: "Include its general knowledge with the answer",
        },
        {
          value: "file_generation_instruction",
          name: "File Generation Instruction",
        },
      ],
      
      gpt_randomness: null,
      introductionTemplates: {
        accurate_ans: `Please answer the query in the same language as the query using the provided context as truthfully as possible. You will be provided with \`Reference Context\` and a \`Query\`. Your task is to analyze the \`Reference Context\` and prepare response for the \`Query\`. If you can not find the answer in the provided context say you don't have knowledge about this question.`,
        generalized_ans: `Please answer the query in the same language as the query using the provided context as truthfully as possible. You will be provided with \`Reference Context\` and a \`Query\`. Your task is to analyze the \`Reference Context\` and prepare response for the \`Query\`. If the \`Reference Context\` is not relevant, use your own knowledge to reply or say that you don't know how to respond if your knowledge is not sufficient to answer.`,
        file_generation_instruction: `For ANY file creation request (e.g. PDF, PPTX, DOCX, XLSX, CSV, HTML, JSON, PY, JS etc.):
- Use the available code-interpreter / code-execution tool for file generation to create the actual file.
- Only respond after the file has been successfully generated and exists.
- Provide the final download option using the generated file link format: \`[Download filename.ext](generated_download_link)\`
- Do NOT provide any progress updates or intermediate steps.
- Do NOT provide any raw code snippets or raw file content in the response when the actual file (download link) was requested.
- Do NOT make up imaginary download links or provide fake file paths. MUST GENERATE ACTUAL FILE.

### For requests requiring web research or current information:
- When the user provides one or more URLs, read those exact pages first using the provider-supported URL retrieval capability: URL Context, Web Fetch, or Web Search direct-page access.
- Use web search to collect additional current information if required, when no URL is provided or the provided pages are insufficient.
- Prefer the exact URLs and domains supplied by the user; do not rely only on search-result snippets or substitute with similarly named websites.
- If a requested page cannot be accessed, state that limitation instead of inventing or assuming its contents.
- Then generate the file using the verified information.

### For multiple file requests:
- Generate all requested files in the same response.
- Provide a separate download link for each generated file.

NEVER say:
- "I will create it"
- "I will prepare it"
- "Please wait"
Or any similar progress update and then ending up not providing the actual file.

Only provide the final result i.e. legit download link for the actual generated file after successful file creation.`,
      },
      isVisionSupported: false,
      isJsonSupported: false,
      supportedTokenSize: {},
      isLangAutoDetect: [
        { text: "true", value: true },
        { text: "false", value: false },
      ],
      displayRuleOptions: [
        {text: "First time visitor", value: "first_time_visitor"},
        {text: "Returned user after 30 days", value: "returned_user_after_30_days"},
        {text: "Returned user after 90 days", value: "returned_user_after_90_days"},
        {text: "Active user", value: "active_user"},
        {text: "Any user", value: "any_user"},
      ],
      triggersOptions: [
        {text: "Session start", value: "session_start"},
        {text: "Keyword detected", value: "keyword_detected"},
        {text: "Immediate", value: "immediate"},
      ],
      orderRules: [
        value => (value !== null && value !== '') || this.$vuetify.lang.t('$vuetify.maira_questionnaire.order_required_message'),
        value => (value > 0) || this.$vuetify.lang.t('$vuetify.maira_questionnaire.order_error_message'),
        value => {
          if (this.profile.questionnaire_config) {
            const occurrences = this.profile.questionnaire_config.filter(config => config.order === value);
            return (occurrences.length <= 1) || this.$vuetify.lang.t('$vuetify.maira_questionnaire.same_order_exists');
          }
          return true;
        }
      ],

      triggersRules: [
        value => (Array.isArray(value) && value.length > 0) || this.$vuetify.lang.t('$vuetify.maira_questionnaire.triggers_required_message')
      ],

      maxAttemptsRules: [
        value => (value !== null && value !== '') || this.$vuetify.lang.t('$vuetify.profile.max_attempts_required'),
        value => (value >= 1) || this.$vuetify.lang.t('$vuetify.profile.max_attempts_error_msg_txt'),
      ],

      questionnaireOptions: [],
      deleteDialog: false,
      configToDeleteIndex: null,
      expandedPanels: null,
      tagChips: [],
      tagsMap: {},
      startTimeMenu: false,
      endTimeMenu: false,
      expandedModerationPanel: null,
      expandedExternalPanel: null, 
      deleteModerationDialog: false,
      deleteModerationIndex: null,
      deleteExternalDialog: false,
      deleteExternalIndex: null,
      expandedResponseFilterPanel: null,
      deleteResponseFilterDialog: false,
      deleteResponseFilterIndex: null,
      actionOptions: [
        { text: "No Action", value: "no_action" },
        { text: "Notification", value: "notification" },
        { text: "Termination", value: "termination" }
      ],
      fetchedCategoryOptions: [],
      fetchedShareableFieldOptions: [],

      isLoadingCategories: false,
      isLoadingShareable: false,

      deleteModerationBlockedDialog: false,
      deleteModerationBlockedList: [],
      originalCategoryNames: {},
      webSearchSupportedProviders: [
        "openai",
        "anthropic",
        "google",
        "gigalogy",
        "maira",
      ],
      unsupportedWebSearchModels: [
        "gpt-3.5-turbo-instruct",
        "gpt-3.5-turbo-0125",
        "gpt-3.5-turbo-0613",
        "gpt-3.5-turbo-16k-0613",
        "gpt-3.5-turbo-1106",
        "gpt-4-vision-preview",
        "gpt-4-turbo-2024-04-09",
        "gpt-4-0125-preview",
        "gpt-4-0613",
        "gpt-4-1106-preview",
        "gpt-4.1-nano-2025-04-14",
        "chatgpt-4o-latest",
      ],
      codeExecutionSupportedProviders: [
        "openai",
        "anthropic",
        "google",
        "gigalogy",
      ],
      unsupportedCodeExecutionModels: [
        "gpt-5.2-pro",
        "gpt-4.1-nano-2025-04-14",
        "gpt-4.1-mini-2025-04-14",
        "gpt-4.1-2025-04-14",
        "gemini-2.5-pro",
        "gemini-2.5-flash",
        "gemini-2.5-flash-lite",
      ],
      availableProfileTagsOptions: [],
      groupInvitationOptions: [],
      isLoadingProfiles: false,
      isLoadingInvitations: false,
      showProfileTags: true,
      originalLiffUrl: '',
      isCreatingInvitation: false,
      isPreviewMode: false,
      displayedLiffUrl: '',
      defaultBaseUrl: getEnv("VUE_APP_MAIRA_APP_URL") || '',
      currentModelMaxOutputTokenSize: null,
    };
  },
  computed: {
    ...mapState({
      profile: state => state.project.selectedGptProfile,
      gptModelList: state => state.project.gptModels,
      gptProviderList: state => state.project.gptProviders,
      gptDatasets: state => state.project.gptDatasets.datasets,
      shareableState: state => state.project?.shareableData ?? null,
      defaultCategoriesState: state => state.project?.defaultCategories ?? [],
      fallbackShareableInfo: state => state.project?.fallbackShareableData ?? {},
      fallbackCategories: state => state.project?.fallbackCategories ?? [],
    }),
    isImageGenerationModel() {
      return String(this.profile?.model || '').toLowerCase().includes('-image');
    },
    isWebSearchSupportedByModel() {
      return this.webSearchSupportedProviders.includes(this.profile?.provider) &&
        !this.unsupportedWebSearchModels.includes(this.profile?.model) &&
        !this.isImageGenerationModel &&
        !!this.profile?.model;
    },
    isCodeExecutionSupportedByModel() {
      return !this.isImageGenerationModel &&
        !!this.profile?.model &&
        this.codeExecutionSupportedProviders.includes(this.profile?.provider) &&
        !this.unsupportedCodeExecutionModels.includes(this.profile?.model) &&
        this.currentModelMaxOutputTokenSize != null &&
        Number(this.currentModelMaxOutputTokenSize) >= MIN_CODE_EXECUTION_OUTPUT_TOKENS;
    },
    minCodeExecutionOutputTokensLabel() {
      return MIN_CODE_EXECUTION_OUTPUT_TOKENS.toLocaleString();
    },
    modelTools() {
      const tools = [];
      if (this.isWebSearchSupportedByModel) {
        tools.push({
          text: this.$vuetify.lang.t('$vuetify.gpt_profile.web_search'),
          value: "web_search",
        });
      }
      if (this.isCodeExecutionSupportedByModel) {
        tools.push({
          text: this.$vuetify.lang.t('$vuetify.gpt_profile.code_execution'),
          value: "code_execution",
        });
      }
      return tools;
    },
    isBackgroundAskRequired() {
      const tools = Array.isArray(this.profile?.tools) ? this.profile.tools : [];
      const model = String(this.profile?.model || '').toLowerCase();

      return tools.includes('code_execution') || model.includes('-image');
    },
    responseFilterTypeOptions() {
      return [
        {
          value: RESPONSE_FILTER_TYPES.PLAIN,
          text: this.$vuetify.lang.t('$vuetify.gpt_profile.response_filters.type_plain_label')
        },
        {
          value: RESPONSE_FILTER_TYPES.REGEX,
          text: this.$vuetify.lang.t('$vuetify.gpt_profile.response_filters.type_regex_label')
        }
      ];
    },
    timezoneOptions() {
      const items = getTimezonePickerItems();
      const current = this.profile?.time_series_config?.default_timezone;
      // Keep a saved zone selectable even if this runtime does not list it.
      return current && !items.some(item => item.value === current)
        ? [{ value: current, text: current }, ...items]
        : items;
    },
    filteredResponseFormatOptions() {
      if (this.isJsonSupported) {
        return [
          { value: 'json_object', text: 'JSON Format' },
          { value: 'text', text: 'Text' }
        ];
      } else {
        return [
          { value: 'text', text: 'Text' }
        ];
      }
    },
    formattedTagsItemsForInclude() {
      return this.getSortedTagsItems(['excluded', 'included']);
    },

    formattedTagsItemsForExclude() {
      return this.getSortedTagsItems(['included', 'excluded']); 
    },

    moderationDeleteMessage() {
      const prefix = this.$vuetify.lang.t('$vuetify.gpt_profile.content_moderation.cannot_delete_msg_prefix')
      const suffix = this.$vuetify.lang.t('$vuetify.gpt_profile.content_moderation.cannot_delete_msg_suffix')

      const names = this.deleteModerationBlockedList[0]?.name || '';

      const listWithDot = names ? ` "${names}".` : '';

      return `${prefix}${listWithDot} ${suffix}`;
    },
    // Returns selected profile tags as an array
    selectedTagsArray() {
      return this.profile?.selected_profile_tags || [];
    },

    // Profile access tag options that includes current profile too
    profileTagsOptions() {
      const options = [...this.availableProfileTagsOptions];
      
      // Add current profile if it has access tags
      const currentTags = this.profile.access_tags;
      if (Array.isArray(currentTags) && currentTags.length > 0) {
        const currentProfileName = this.profile.profileName;
        const currentId = this.profile.id;
        
        // Check if current profile already exists
        const existingIndex = options.findIndex(opt => opt.profileId === currentId);
        
        const profileOption = {
          label: currentProfileName,
          tags: [...currentTags],
          profileId: currentId,
          isCurrent: true
        };
        
        if (existingIndex !== -1) {
          // Update with latest tags
          options[existingIndex] = profileOption;
        } else {
          // Add new entry
          options.unshift(profileOption);
        }
      }
      
      return options;
    },
    isLiffEnabled() {
      const configs = this.profile.external_system_configs || [];

      // LIFF is enabled if any config has no redirect_triggering_categories
      const isEnabled = configs.some(({ redirect_triggering_categories }) =>
        !Array.isArray(redirect_triggering_categories) ||
        redirect_triggering_categories.length === 0
      );

      if (!isEnabled) {
        this.clearLiffConfig();
        return false;
      }

      this.ensureBaseUrl();
      return true;
    },
    liffDisabledMessage() {
      if (this.isLiffEnabled) return '';
      
      return this.$vuetify.lang.t('$vuetify.gpt_profile.liff_endpoint_url_config.config_disable');
    },
    isGroupInvitationRequired() {
      const liffId = this.profile?.liff_endpoint_url_config?.liff_id;
      return !!(liffId && String(liffId).trim() !== '');
    },
    isLiffIdRequired() {
      const token =
        this.profile?.liff_endpoint_url_config?.group_invitation_token;

      return !!(token && String(token).trim() !== '');
    },
    // Reports Web Search and Code Execution status independently rather than one combined supported/unsupported claim -  the per-tool status below is always derived from the same source of truth the tools select itself uses, so it can't drift.
    toolsTooltipText() {
      const minTokens = this.minCodeExecutionOutputTokensLabel;

      if (!this.webSearchSupportedProviders.includes(this.profile?.provider)) {
        return this.$vuetify.lang.t('$vuetify.gpt_profile.tools_provider_required');
      }

      if (!this.profile?.model) {
        return this.$vuetify.lang.t('$vuetify.gpt_profile.tools_model_required')
          .replace('{min_tokens}', minTokens);
      }

      const model = this.profile.model;
      const hint = this.$vuetify.lang.t('$vuetify.gpt_profile.tools_hint');

      const webSearchLine = this.$vuetify.lang.t(this.isWebSearchSupportedByModel
        ? '$vuetify.gpt_profile.tools_web_search_supported'
        : '$vuetify.gpt_profile.tools_web_search_unsupported'
      ).replace('{model}', model);

      const codeExecutionLine = this.$vuetify.lang.t(this.isCodeExecutionSupportedByModel
        ? '$vuetify.gpt_profile.tools_code_execution_supported'
        : '$vuetify.gpt_profile.tools_code_execution_unsupported'
      ).replace('{model}', model).replace('{min_tokens}', minTokens);

      return `${hint} ${webSearchLine} ${codeExecutionLine}`;
    },
    // Reactive rules for Temperature
    dynamicTemperatureRules() {
      return [
        value => {
          if (this.temperatureMeta.required && validations.isEmpty(value)) {
            return this.required(this.$vuetify.lang.t('$vuetify.gpt_profile.temperature'))(value);
          }
          return true;
        },
        value => {
          if (validations.isEmpty(value)) return true;
          const num = Number(value);
          return (Number.isFinite(num) && num <= Number(this.temperatureMeta.max)) || `${this.$vuetify.lang.t('$vuetify.gpt_profile.max_dynamic')}${this.temperatureMeta.max}`;
        },
        value => {
          if (validations.isEmpty(value)) return true;
          const num = Number(value);
          return (Number.isFinite(num) && num >= Number(this.temperatureMeta.min)) || `${this.$vuetify.lang.t('$vuetify.gpt_profile.min_dynamic')}${this.temperatureMeta.min}`;
        }
      ];
    },
    // Reactive rules for Top P
    dynamicTopPRules() {
      return [
        value => {
          if (this.topPMeta.required && validations.isEmpty(value)) {
            return this.required(this.$vuetify.lang.t('$vuetify.gpt_profile.top_p'))(value);
          }
          return true;
        },
        value => {
          if (validations.isEmpty(value)) return true;
          const num = Number(value);
          return (Number.isFinite(num) && num <= Number(this.topPMeta.max)) || `${this.$vuetify.lang.t('$vuetify.gpt_profile.max_dynamic')}${this.topPMeta.max}`;
        },
        value => {
          if (validations.isEmpty(value)) return true;
          const num = Number(value);
          return (Number.isFinite(num) && num >= Number(this.topPMeta.min)) || `${this.$vuetify.lang.t('$vuetify.gpt_profile.min_dynamic')}${this.topPMeta.min}`;
        }
      ];
    }
  },
  watch: {

    profile: {
      deep: true,
      async handler(value) {
        this.debouncedCalculateMaxToken();

        if (!value.chat_history_length && value.chat_history_length !== 0) {
          this.$set(this.profile, "chat_history_length", 0);
        }
        if (value.is_personalizer_only === undefined) {
          this.$set(this.profile, "is_personalizer_only", false);
        }
        if (value.is_auto_evaluation === undefined) {
          this.$set(this.profile, "is_auto_evaluation", false);
        }
        if (value.is_citation_enabled === undefined) {
          this.$set(this.profile, "is_citation_enabled", false);
        }
        const localProfile = this.profile;
        this.UPDATE_GPT_PROFILE_STATE({ profile: localProfile });
      },
    },
    isJsonSupported(newValue) {
      if (!newValue && this.profile.responseFormat === 'json_object') {
        this.profile.responseFormat = 'text';
      }
    },
    "profile.model": {
      handler(newModel) {
        if (newModel) {
          this.gptModelDetails(this.isManualModelChange);
        }
        this.isManualModelChange = false;
      },
      immediate: true
    },
    gpt_randomness: {
      handler(newValue) {
        if (newValue && this.introductionTemplates[newValue]) {
          this.$set(
            this.profile,
            "intro",
            this.introductionTemplates[newValue],
          );
        }
      },
    },
    gptDatasets: {
      immediate: true,
      handler(datasets) {
        if (datasets && datasets.length > 0) {
          this.tagsMap = {};
          datasets.forEach((dataset, index) => {
            const key = dataset.name?.trim() || `dataset_${index + 1}`;
            this.tagsMap[key] = dataset.tags || [];
          });
          
          // Update tagChips with all unique tags from all datasets
          const allTags = new Set();
          Object.values(this.tagsMap).forEach(tags => {
            tags.forEach(tag => allTags.add(tag));
          });
          this.tagChips = Array.from(allTags);
        }
      }
    },
    'profile.external_system_configs': {
      deep: true,
      handler(_newVal) {
        if (this.countEmptyRedirects() <= 1) {
          this.clearOtherExternalValidationErrors(null);
        }
      }
    },
    'profile.id': {
      immediate: true,
      handler(newVal) {
        this.rememberedTools = null;
        this.currentModelMaxOutputTokenSize = null;

        if (newVal) {
          // Set default base_url if not already set
          if (!this.profile.liff_endpoint_url_config) {
            this.$set(this.profile, 'liff_endpoint_url_config', {
              liff_id: '',
              group_invitation_token: '',
              base_url: this.defaultBaseUrl
            });
          } else if (!this.profile.liff_endpoint_url_config.base_url) {
            this.$set(this.profile.liff_endpoint_url_config, 'base_url', this.defaultBaseUrl);
          }
          if (this.profile.liff_endpoint_url) {
            this.originalLiffUrl = this.profile.liff_endpoint_url;
            this.displayedLiffUrl = this.profile.liff_endpoint_url;
          } else {
            this.originalLiffUrl = '';
            this.displayedLiffUrl = '';
          }
        }
      },
    },
    // Preview/Undo URL
    'profile.liff_endpoint_url_config.liff_id'(newVal) {
      if (!newVal || String(newVal).trim() === '') {
        if (this.profile && this.profile.liff_endpoint_url_config) {
          this.$set(
            this.profile.liff_endpoint_url_config,
            'group_invitation_token',
            ''
          );
        }
      }

      this.resetPreviewIfNeeded();
    },
    'profile.liff_endpoint_url_config.base_url': 'resetPreviewIfNeeded',
    'profile.liff_endpoint_url_config.group_invitation_token'() {
      this.resetPreviewAndEmitTags();
      this.populateInvitationTags();
    },
    'profile.selected_profile_tags': {
      deep: true,
      handler() {
        this.resetPreviewAndEmitTags();
      }
    },
    isLiffEnabled: {
      handler(newVal) {
        this.$emit('liff-enabled-changed', newVal);
      },
      immediate: true
    },
  },
  created() {
      this.debouncedCalculateMaxToken = debounce(this.calculateMaxToken, 1000);

      if (this.profile) {
        if (!this.profile.content_moderation || typeof this.profile.content_moderation !== 'object') {
          this.$set(this.profile, 'content_moderation', { is_filtering_enabled: false, categories: [] });
        } else if (!Array.isArray(this.profile.content_moderation.categories)) {
          this.$set(this.profile.content_moderation, 'categories', []);
        }

        if (!Array.isArray(this.profile.external_system_configs)) {
          this.$set(this.profile, 'external_system_configs', []);
        }

        if (!Array.isArray(this.profile.response_filters)) {
          this.$set(this.profile, 'response_filters', []);
        }

        if (!this.profile.liff_endpoint_url_config || typeof this.profile.liff_endpoint_url_config !== 'object') {
          this.$set(this.profile, 'liff_endpoint_url_config', {
            liff_id: '',
            group_invitation_token: '',
            base_url: ''
          });
        }

        if (!this.profile.liff_endpoint_url_config.base_url) {
          this.$set(
            this.profile.liff_endpoint_url_config,
            'base_url',
            this.defaultBaseUrl || ''
          );
        }

        if (!Array.isArray(this.profile.selected_profile_tags)) {
          this.$set(this.profile, 'selected_profile_tags', []);
        }

        if (!this.profile.time_series_config || typeof this.profile.time_series_config !== 'object') {
          this.$set(this.profile, 'time_series_config', {
            require_user_confirmation: false,
            show_reference_fields: false,
            default_timezone: null
          });
        }
      }

      this.fetchShareableFields();
      this.fetchCategoryOptions();
  },
  async mounted() {
    this.fetchTags();
    this.fetchQuestionnaires();
    this.fetchAllProfiles();
    await this.fetchGroupInvitations();
  },
  methods: {
    ...mapActions("project", [UPDATE_GPT_PROFILE_STATE, GET_GPT_MODEL_DETAILS, "GET_GPT_DATASETS", MAIRA_QUESTIONNAIRE, FETCH_SHAREABLE_DATA, GET_DEFAULT_CATEGORIES, GET_GPT_PROFILES]),
    ...mapActions("member", [GET_PUBLIC_INVITATIONS_LIST, UPDATE_PUBLIC_INVITATION_URL, GENERATE_PUBLIC_INVITATION_URL]),
    toolItemHintText(toolValue) {
      if (toolValue !== 'code_execution') {
        return this.$vuetify.lang.t('$vuetify.gpt_profile.web_search_enable_hint');
      }
      return this.$vuetify.lang.t('$vuetify.gpt_profile.code_execution_enable_hint')
        .replace('{min_tokens}', this.minCodeExecutionOutputTokensLabel);
    },
    checkTextTruncate(event, filter, idx) {
      const element = event.target;
      const key = `filter-${idx}`;
      this.$set(this.truncatedFilterTitles, key, element.scrollWidth > element.clientWidth);
    },
    required(fieldName) {
      return (value) => {
        return !!value || `${fieldName}${this.$vuetify.lang.t('$vuetify.validation.required_error_msg_txt')}`;
      };
    },
    keywordsRules(config) {
      return [
        value => {
          if (this.isKeywordDetected(config.triggers)) {
            return (Array.isArray(value) && value.length > 0) || this.$vuetify.lang.t('$vuetify.profile.keyword_error_msg_txt')
          }
          return true;
        }
      ];
    },
    positiveIntegerRule(value) {
      if (!value) return true;
      return (Number.isInteger(Number(value)) && value > 0) || this.$vuetify.lang.t('$vuetify.gpt_profile.positive_required_message');
    },
    validateForm() {
      const currentKey = `${this.profile.provider}/${this.profile.model}`;

      // You need BOTH checks here:
      if (
        this.modelDetailsKey !== currentKey ||
        this.modelDetailsStatus !== "ready"
      ) {
        const isError = this.modelDetailsStatus === "error";
        this.modelFieldError = isError 
          ? this.$vuetify.lang.t("$vuetify.gpt_profile.model_configuration_not_found")
          : "";
          
        this.$nextTick(() => {
          if (this.$refs.modelInput && typeof this.$refs.modelInput.focus === 'function') {
            this.$refs.modelInput.focus();
          }
        });
        this.$refs.profile_form.validate();
        
        return false;
      }
      
      this.modelFieldError = '';

      this.expandPanelsWithErrors();
      let isValid = this.$refs.profile_form.validate();
      
      if (isValid) {
        let appliedDefault = false;
        if (validations.isEmpty(this.profile.temperature)) {
          this.$set(this.profile, "temperature", this.temperatureMeta.default);
          appliedDefault = true;
        }
        if (validations.isEmpty(this.profile.top_p)) {
          this.$set(this.profile, "top_p", this.topPMeta.default);
          appliedDefault = true;
        }
        
        if (appliedDefault) {
          isValid = this.$refs.profile_form.validate();
        }
      }

      if (!this.showConfig) {
        this.$delete(this.profile, "questionnaire_config");
      }

      return isValid;
    },
    onProfileTagsChange(val) {
      const trimmed = val.map(tag => tag.trim()).filter(tag => tag !== "");
      this.profile.access_tags = Array.from(new Set(trimmed));
    },
    onStopTagsChange(val) {
      let newVal = val
      if (newVal.length > 4) {
        newVal = newVal.slice(0, 4);
      }
      const trimmed = newVal.map(tag => tag.trim()).filter(tag => tag !== "");
      this.profile.stop = Array.from(new Set(trimmed));
    },
    includesTagsChange(val) {
      const trimmed = val.map(tag => tag.trim()).filter(tag => tag !== "");
      this.profile.datasetTags.includes = Array.from(new Set(trimmed));
    },
    excludesTagsChange(val) {
      const trimmed = val.map(tag => tag.trim()).filter(tag => tag !== "");
      this.profile.datasetTags.excludes = Array.from(new Set(trimmed));
    },
    sanitizeRedirectionMessage(cfg) {
      if (!cfg || typeof cfg.redirection_message !== 'string') return;
      const trimmed = cfg.redirection_message.trim();
      if (trimmed === '') {
        cfg.redirection_message = '';
        return;
      }
      // If the trimmed value contains at least one letter or number, keep it; otherwise clear it.
      const hasLetterOrNumber = /\p{L}|\p{N}/u.test(trimmed);
      cfg.redirection_message = hasLetterOrNumber ? trimmed : '';
    },
    sanitizeTerminationMessage(cfg) {
      if (!cfg || typeof cfg.termination_message !== 'string') return;
      const trimmed = cfg.termination_message.trim();
      if (trimmed === '') {
        cfg.termination_message = '';
        return;
      }
      // If the trimmed value contains at least one letter or number, keep it; otherwise clear it.
      const hasLetterOrNumber = /\p{L}|\p{N}/u.test(trimmed);
      cfg.termination_message = hasLetterOrNumber ? trimmed : '';
    },
    hasTerminationAction(cfg) {
      return !!cfg && Object.values(cfg.severity_actions || {}).includes('termination');
    },
    removeStop(item) {
      this.profile.stop.splice(this.profile.stop.indexOf(item), 1)
    },
    removeIncludes(item){
      this.profile.datasetTags.includes.splice(this.profile.datasetTags.includes.indexOf(item), 1)
    },
    removeAccessTags(item){
      this.profile.access_tags.splice(this.profile.access_tags.indexOf(item), 1)
    },
    removeExcludes(item){
      this.profile.datasetTags.excludes.splice(this.profile.datasetTags.excludes.indexOf(item), 1)
    },
    onSessionCloseTriggersChange(val) {
      const trimmed = val?.map(text => text.trim())?.filter(text => text !== "");
      this.profile.session_close_trigger_config.triggers = Array.from(new Set(trimmed));
    },
    removeSessionCloseTrigger(item) {
      this.profile.session_close_trigger_config.triggers?.splice(this.profile.session_close_trigger_config.triggers?.indexOf(item), 1)
    },
    getFullFormOfLanguage(code) {
      const languageMap = {
        en: 'English',
        ja: 'Japanese', 
        ko: 'Korean'
      };
      return languageMap[code] || code;
    },
    removeLanguage(item) {
      this.profile.dataset_languages = this.profile.dataset_languages.filter(
        lang => lang !== item
      );
    },
    async calculateMaxToken() {
      try {
        const tokenizer = await this.$initTokenizer("gpt-3.5-turbo");

        const introTokens = this.profile.intro
          ? tokenizer.encode(this.profile.intro)
          : [];
        const systemTokens = this.profile.system
          ? tokenizer.encode(this.profile.system)
          : [];
        const completionToken = Number.isNaN(
          parseInt(this.profile.completionToken),
        )
          ? 0
          : parseInt(this.profile.completionToken);

        // tokenizer.free();

        const model = this.profile.model;
        if (!Object.prototype.hasOwnProperty.call(this.supportedTokenSize, model)) {
          // Handle undefined model. Return a safe default.
          return 0;
        }

        const modelWindow = this.supportedTokenSize[model];
        const availableTokens =
          modelWindow -
          (introTokens.length +
            systemTokens.length +
            completionToken +
            PROMPT_TEMPLATE_SEPARATORS_TOKEN_COUNT);
            this.maxToken = Math.ceil(availableTokens - modelWindow * 0.1);
      } catch (error) {
        console.error("Error in calculateMaxToken:", error);
        // Handle error appropriately
      }
    },
    resetValidation() {
      if (this.$refs.profile_form) {
        this.$refs.profile_form.resetValidation();
      }
      this.UPDATE_GPT_PROFILE_STATE({
        profile: GptProfileMapper.mapBackendToFrontend(),
      });
      this.maxToken = 32000;
      this.profileLoader = true;
      this.gpt_randomness = null;
      this.temperatureMeta = { min: 0, max: 2, default: 0, required: false };
      this.topPMeta = { min: 0, max: 1, default: 1, required: false };
      this.modelChangeToken = 0;
      this.modelDetailsStatus = "idle";
      this.modelDetailsKey = null;
      this.modelFieldError = "";
      this.currentModelMaxOutputTokenSize = null;
      // this.completionTokenRules = [
      //   value => value <= 2500 || "Max value is 2500",
      //   value => value >= 0 || "Min value 0",
      // ];
      this.searchTokenRules = [
        value => value <= this.maxToken || `${this.$vuetify.lang.t('$vuetify.gpt_profile.max_dynamic')}${this.maxToken}`,
        value => value >= 0 || this.$vuetify.lang.t('$vuetify.gpt_profile.min_0'),
      ];
      this.chatHistoryLengthRules = [
        value => value >= 0 || this.$vuetify.lang.t('$vuetify.gpt_profile.min_zero_or_greater'),
        value => Number.isInteger(Number(value)) || this.$vuetify.lang.t('$vuetify.gpt_profile.integer_required'),
      ];
      if (this.modelDetails) {
        this.updateTokenLimits(this.modelDetails);
      }
    },
    async fetchQuestionnaires() {
      try {
        const response = await this.MAIRA_QUESTIONNAIRE({
          project_id: this.$route.query.pid,
          start: 0,
          size: 100,
          active: true
        });

        const resData = response.data.detail.response.questionnaires || [];
        // id dropdown options
        this.questionnaireOptions = resData.map(questionnaire => ({
          id: questionnaire.id,
          label: `${questionnaire.name}`
        }));
      } catch (error) {
        console.error("Error fetching questionnaires:", error);
      }
    },
    async gptModelDetails(isManualChange = false) {
      const provider = this.profile.provider;
      const model = this.profile.model;
      const requestKey = `${provider}/${model}`;

      this.modelChangeToken++;
      const expectedToken = this.modelChangeToken;
      
      this.modelDetailsKey = requestKey;
      this.modelDetailsStatus = "loading";
      this.isLoading = true;
      this.$emit('model-status-changed', this.modelDetailsStatus);

      try {
        const response = await this.GET_GPT_MODEL_DETAILS({
          project_id: this.$route.query.pid,
          provider,
          model
        });

        if (this.modelChangeToken !== expectedToken) return null;

        const modelDetails = response.data?.detail?.response;
        
        if (modelDetails) {
          this.isVisionSupported = modelDetails.is_vision_supported;
          this.isJsonSupported = modelDetails.is_json_supported;
          this.updateTokenLimits(modelDetails);
          this.updateHyperparams(modelDetails, isManualChange);
        } else {
          throw new Error("Model constraints are missing");
        }

        this.isLoading = false;
        this.modelDetailsStatus = "ready";
        this.$emit('model-status-changed', this.modelDetailsStatus);

        return modelDetails;
      } catch (error) {
        if (this.modelChangeToken === expectedToken) {
          this.isLoading = false;
          this.modelDetailsStatus = "error";
          this.$emit('model-status-changed', this.modelDetailsStatus);
          
          this.isVisionSupported = false;
          this.isJsonSupported = false;
          this.updateHyperparams(null, isManualChange);

          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message: this.$vuetify.lang.t("$vuetify.gpt_profile.model_configuration_not_found"),
              color: "error",
            },
            { root: true }
          );
          
          this.modelDetails = null;
        }
        return null;
      }
    },
    async fetchTags() {
      try {
        await this.GET_GPT_DATASETS({
          project_id: this.$route.query.pid,
          size: 100
        });
      } catch (error) {
        console.error("Error fetching datasets for tags:", error);
      }
    },

    tagIncluded(tag) {
      return this.profile.datasetTags?.includes?.includes(tag) || false;
    },

    tagExcluded(tag) {
      return this.profile.datasetTags?.excludes?.includes(tag) || false;
    },

    toggleIncludeTag(tag) {
      this.toggleTag(tag, 'include');
    },

    toggleExcludeTag(tag) {
      this.toggleTag(tag, 'exclude');
    },
    getTagCursor(tag, context, datasetName = null) {
      // Check if the specific tag can be selected
      if (context === 'include' && !this.canIncludeTag(tag)) {
        return 'not-allowed';
      }
      if (context === 'exclude' && !this.canExcludeTag(tag)) {
        return 'not-allowed';
      }
      
      // Check dataset-level disabling (only if ALL tags in dataset are contaminated)
      if (datasetName) {
        if (context === 'include' && this.datasetDisabledForIncludes(datasetName)) {
          return 'not-allowed';
        }
        if (context === 'exclude' && this.datasetDisabledForExcludes(datasetName)) {
          return 'not-allowed';
        }
      }
      
      return 'pointer';
    },

    getTagOpacity(tag, context, datasetName = null) {
      // Check if the specific tag can be selected
      if (context === 'include' && !this.canIncludeTag(tag)) {
        return 0.3;
      }
      if (context === 'exclude' && !this.canExcludeTag(tag)) {
        return 0.3;
      }
      
      if (datasetName) {
        if (context === 'include' && this.datasetDisabledForIncludes(datasetName)) {
          return 0.3;
        }
        if (context === 'exclude' && this.datasetDisabledForExcludes(datasetName)) {
          return 0.3;
        }
      }
      
      return 1;
    },
    updateTokenLimits(modelDetails) {
      const supportedTokenSize = modelDetails.supported_token_size;
      const maxOutputTokenSize = modelDetails.max_output_token_size;
      this.currentModelMaxOutputTokenSize = maxOutputTokenSize;

      const isCodeExecutionEnabled = () =>
        Array.isArray(this.profile?.tools) &&
        this.profile.tools.includes('code_execution');

      // Authoritative recompute now that this model's real maxOutputTokenSize is known - see applySupportedTools() for why this is the one place profile.tools gets derived (it always assigns an array, so no separate normalization is needed here).
      this.applySupportedTools();

      this.$set(this.supportedTokenSize, this.profile.model, supportedTokenSize);
      this.completionTokenRules = [
        value => this.required(this.$vuetify.lang.t('$vuetify.gpt_profile.completion_token'))(value),
        value => value > 0 || this.$vuetify.lang.t('$vuetify.gpt_profile.completion_token_positive_message'),
        value => !value ||
                 !isCodeExecutionEnabled() ||
                  !this.isCodeExecutionSupportedByModel ||
                  Number(value) >= MIN_CODE_EXECUTION_OUTPUT_TOKENS ||
                  this.$vuetify.lang.t('$vuetify.gpt_profile.code_execution_completion_token_minimum')
                    .replace('{min_tokens}', this.minCodeExecutionOutputTokensLabel),
        value => Number(value) <= Number(maxOutputTokenSize) || `${this.$vuetify.lang.t('$vuetify.gpt_profile.max_dynamic')}${maxOutputTokenSize}`,
      ];
    },
    addQuestionnaireConfig() {
      if (!this.showConfig) return;

      // if (!this.profile.questionnaire_config) {
      //   this.$set(this.profile, 'questionnaire_config', []);
      // }
      
      const newConfig = {
        questionnaire_id: "",
        display_rule: '',
        frequency: {
          max_attempts: 1,
          retry_interval_days: 0
        },
        triggers: [],
        order: 1,
        keywords: [],
        use_intelligent_detection: false,
        active: true,
        intro_message: "",
        completion_message: ""
      };
      
      this.profile.questionnaire_config.push(newConfig);
      // Expand every newly added panels when they are added
      this.expandedPanels = this.profile.questionnaire_config.length - 1;
    },

    onKeywordsChange(configIndex, keywords) {
      const trimmed = keywords.map(keyword => keyword.trim()).filter(keyword => keyword !== "");
      this.$set(this.profile.questionnaire_config[configIndex], 'keywords', Array.from(new Set(trimmed)));
    },

    removeKeyword(configIndex, keyword) {
      const config = this.profile.questionnaire_config[configIndex];
      const keywordIndex = config.keywords.indexOf(keyword);
      if (keywordIndex > -1) {
        config.keywords.splice(keywordIndex, 1);
      }
    },
    customFilter(item, queryText) {
      if (!item || !queryText)
        return true;
      const searchText = queryText.toLowerCase();
      const modelName = (item.model || '').toLowerCase();
      const displayName = (item.display || '').toLowerCase();
        return modelName.includes(searchText) ||
            displayName.includes(searchText);
    },
    getTagState(tag) {
      if (this.tagIncluded(tag)) {
        return 'included';
      } else if (this.tagExcluded(tag)) {
        return 'excluded';
      } else {
        return 'available';
      }
    },

    getTagColor(tag, context = 'dropdown') {
      const state = this.getTagState(tag);
      
      if (context === 'dropdown') {
        switch (state) {
          case 'included':
            return 'primary';
          case 'excluded':
            return 'error';
          default:
            return 'default';
        }
      }
       
      return context === 'exclude' ? 'error' : 'primary';
    },
    canToggleTag(tag, mode) {
      const isConflict =
        mode === 'include' ? this.tagExcluded(tag) : this.tagIncluded(tag);

      if (isConflict) return false;

      const baseTags =
        mode === 'include'
          ? this.profile.datasetTags?.excludes || []
          : this.profile.datasetTags?.includes || [];

      const contaminatedTags = this.getAllContaminatedTags(baseTags);

      return !contaminatedTags.has(tag);
    },
    canIncludeTag(tag) {
      return this.canToggleTag(tag, 'include');
    },

    canExcludeTag(tag) {
      return this.canToggleTag(tag, 'exclude');
    },
    getTagPriority(tag, order) {
      const { includes, excludes } = this.profile.datasetTags;

      if (includes?.includes(tag)) {
        return [order.indexOf("included"), includes.indexOf(tag)];
      } else if (excludes?.includes(tag)) {
        return [order.indexOf("excluded"), excludes.indexOf(tag)];
      } else {
        return [order.indexOf("available"), this.tagChips.indexOf(tag)]; 
        // fallback: original position
      }
    },

    getSortedTagsItems(order = ["included", "excluded", "available"]) {
      return Object.entries(this.tagsMap).map(([datasetName, tagsList]) => {
        const sortedTagsList = [...tagsList].sort((tagOne, tagTwo) => {
          const [tagGroupOne, idxOne] = this.getTagPriority(tagOne, order);
          const [tagGroupTwo, idxTwo] = this.getTagPriority(tagTwo, order);

          return tagGroupTwo - tagGroupOne || idxOne - idxTwo;
        });

        return { dataset: datasetName, tags: sortedTagsList };
      });
    },
    toggleTag(tag, type) {
      const otherType = type === 'include' ? 'excludes' : 'includes';
      if (this.profile.datasetTags[otherType]?.includes(tag)) return;

      if (!this.profile.datasetTags[`${type}s`]) {
        this.$set(this.profile.datasetTags, `${type}s`, []);
      }

      const index = this.profile.datasetTags[`${type}s`].indexOf(tag);
      if (index === -1) {
        this.profile.datasetTags[`${type}s`].push(tag);
      } else {
        this.profile.datasetTags[`${type}s`].splice(index, 1);
      }
    },
    truncateDatasetName(datasetName) {
      if (!datasetName) return '';
      const upperCaseName = datasetName.toUpperCase();
      return upperCaseName.length <= MAX_DATASET_NAME_LENGTH
        ? upperCaseName
        : `${upperCaseName.substring(0, MAX_DATASET_NAME_LENGTH)}...`;
    },

    datasetNameTruncated(datasetName) {
      if (!datasetName) return false;
      return datasetName.toUpperCase().length > MAX_DATASET_NAME_LENGTH;
    },
    // dataset disabled check
    datasetDisabled(datasetName, context) {
      const availableTagsInDataset = this.tagsMap[datasetName] || [];
      if (availableTagsInDataset.length === 0) return false;

      const conflictingActionType = context === CONTAMINATION_CONTEXTS.INCLUDE
        ? CONTAMINATION_CONTEXTS.EXCLUDE
        : CONTAMINATION_CONTEXTS.INCLUDE;

      const userSelectedOppositeTags = this.profile.datasetTags?.[`${conflictingActionType}s`] || [];
      const contaminatedTags = this.getAllContaminatedTags(userSelectedOppositeTags);

      return availableTagsInDataset.every(tag => contaminatedTags.has(tag));
    },

    datasetDisabledForIncludes(datasetName) {
      return this.datasetDisabled(datasetName, CONTAMINATION_CONTEXTS.INCLUDE);
    },

    datasetDisabledForExcludes(datasetName) {
      return this.datasetDisabled(datasetName, CONTAMINATION_CONTEXTS.EXCLUDE);
    },
    getAllContaminatedTags(startingTags) {
      const contaminatedTags = new Set();
      
      if (!Array.isArray(startingTags) || startingTags.length === 0) {
        return contaminatedTags;
      }
      
      startingTags.forEach(startingTag => {
        Object.entries(this.tagsMap).forEach(([datasetName, datasetTags]) => {
          if (Array.isArray(datasetTags) && datasetTags.includes(startingTag)) {
            datasetTags.forEach(tag => contaminatedTags.add(tag));
          }
        });
      });
      
      return contaminatedTags;
    },
    tagContaminated(tag, context) {
      if (!Object.values(CONTAMINATION_CONTEXTS).includes(context)) {
        return false;
      }
      
      const oppositeTags = this.profile.datasetTags?.[
        context === CONTAMINATION_CONTEXTS.INCLUDE ? 'excludes' : 'includes'
      ] || [];
      
      return this.getAllContaminatedTags(oppositeTags).has(tag);
    },
    handleKeydown(event) {
      if (this.showtags) {
        event.preventDefault();
      }
    },
    isKeywordDetected(triggers) {
      return Array.isArray(triggers) && triggers.includes("keyword_detected");
    },
    retryIntervalRules(config) {
      return [
        value => {
          const num = Number(value);

          if (config.frequency.max_attempts > 1) {
            if (value === null || value === '' || isNaN(num)) {
              return this.$vuetify.lang.t('$vuetify.profile.retry_interval_error_msg_txt');
            }
            return num >= 0 || this.$vuetify.lang.t('$vuetify.profile.retry_interval_error_txt');
          }

          if (value === null || value === '') return true;
          return num >= 0 || this.$vuetify.lang.t('$vuetify.profile.retry_interval_error_txt');
        },
      ];
    },
    getAvailableQuestionnaireOptions() {
      return this.questionnaireOptions;
    },
    confirmDeleteConfig(index) {
      this.configToDeleteIndex = index;
      this.deleteDialog = true;
    },

    deleteConfigConfirmed() {
      if (this.configToDeleteIndex !== null) {
        this.profile.questionnaire_config.splice(this.configToDeleteIndex, 1);

        this.configToDeleteIndex = null;
        this.deleteDialog = false;
      }
    },
    getPanelTitle(config, index) {
      const selected = this.questionnaireOptions.find(
        item => item.id === config.questionnaire_id
      );

      if (!selected) {
        return `${this.$vuetify.lang.t('$vuetify.profile.config')} ${index + 1}`;
      }

      const allConfigs = this.profile.questionnaire_config;
      const sameQuestionnaires = allConfigs.filter(
        c => c.questionnaire_id === config.questionnaire_id
      );

      const duplicateIndex = sameQuestionnaires.indexOf(config);

      const suffix = sameQuestionnaires.length > 1
        ? ` ${duplicateIndex + 1}`
        : '';

      return `${selected.label}${suffix}`;
    },
    expandPanelsWithErrors() {
      const hasError = (config) => 
        !config.questionnaire_id ||
        !config.display_rule ||
        (this.isKeywordDetected(config.triggers) && (!config.keywords?.length)) ||
        config.order == null;

        const panelsToExpand = (this.profile.questionnaire_config || [])
        .map((config, index) => (hasError(config) ? index : -1))
        .filter(index => index !== -1);

      if (panelsToExpand.length) {
        this.expandedPanels = panelsToExpand.length === 1 ? panelsToExpand[0] : panelsToExpand;
      }
    },

    onContentModerationKeywordsChange(configIndex, keywords) {
      if (!this.profile.content_moderation || typeof this.profile.content_moderation !== 'object') {
        this.$set(this.profile, 'content_moderation', { is_filtering_enabled: false, categories: [] });
      }

      if (!Array.isArray(this.profile.content_moderation.categories)) {
        this.$set(this.profile.content_moderation, 'categories', []);
      }

      if (!this.profile.content_moderation.categories[configIndex]) {
        this.$set(this.profile.content_moderation.categories, configIndex, { name: '', keywords: [] });
      }

      const normalized = (Array.isArray(keywords) ? keywords : [])
        .map(k => (typeof k === 'string' ? k.trim().toLowerCase() : ''))
        .filter(k => k !== '');

      const unique = Array.from(new Set(normalized));

      this.$set(this.profile.content_moderation.categories[configIndex], 'keywords', unique);
    },

    removeContentModerationKeyword(configIndex, keyword) {
      if (!Array.isArray(this.profile.content_moderation?.categories)) return;

      const config = this.profile.content_moderation.categories[configIndex];
      if (!config || !Array.isArray(config.keywords)) return;

      const keywordIndex = config.keywords.indexOf(keyword);
      if (keywordIndex > -1) {
        config.keywords.splice(keywordIndex, 1);
        this.$set(this.profile.content_moderation.categories, configIndex, { ...config });
      }
    },

    addModerationConfig() {
      const newCfg = {
        _tmpId: Date.now(),
        name: '',
        keywords: [],
        detection_rules: [],
        severity_actions: { low: 'no_action', medium: 'no_action', high: 'no_action' },
        termination_message: ''
      };

      if (!this.profile.content_moderation || typeof this.profile.content_moderation !== 'object') {
        this.$set(this.profile, 'content_moderation', { is_filtering_enabled: false, categories: [] });
      }

      if (!Array.isArray(this.profile.content_moderation.categories)) {
        this.$set(this.profile.content_moderation, 'categories', []);
      }

      this.profile.content_moderation.categories.push(newCfg);

      this.$nextTick(() => {
        const newIndex = this.profile.content_moderation.categories.length - 1;
        this.expandedModerationPanel = newIndex;
      });
    },

    confirmModerationDelete(idx) {
      this.deleteModerationIndex = idx;

      const category = this.profile?.content_moderation?.categories?.[idx];
      const catName = category?.name ? String(category.name).trim() : null;

      if (!catName) {
        this.deleteModerationDialog = true;
        return;
      }

      const usingConfigs = (this.profile?.external_system_configs || [])
        .map((cfg, i) => ({
          idx: i,
          name: cfg.name || `${this.$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.external_config_name')} ${i + 1}`,
          used: Array.isArray(cfg.redirect_triggering_categories) && cfg.redirect_triggering_categories.includes(catName)
        }))
        .filter(x => x.used);

      if (usingConfigs.length > 0) {
        this.deleteModerationBlockedList = usingConfigs;
        this.deleteModerationBlockedDialog = true;
      } else {
        this.deleteModerationDialog = true;
      }
    },

    deleteModerationConfirmed() {
      const idx = this.deleteModerationIndex;
      if (idx == null) return;

      if (!Array.isArray(this.profile?.content_moderation?.categories)) {
        this.deleteModerationDialog = false;
        this.deleteModerationIndex = null;
        return;
      }

      const cat = this.profile.content_moderation.categories[idx];
      const catName = cat?.name ? String(cat.name).trim() : null;
      const isReferenced = catName && (this.profile.external_system_configs || []).some(cfg =>
        Array.isArray(cfg.redirect_triggering_categories) && cfg.redirect_triggering_categories.includes(catName)
      );

      if (isReferenced) {
        this.deleteModerationBlockedList = (this.profile.external_system_configs || [])
          .map((cfg, i) => ({ idx: i, name: cfg.name || `${this.$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.external_config_name')} ${i + 1}`, used: Array.isArray(cfg.redirect_triggering_categories) && cfg.redirect_triggering_categories.includes(catName) }))
          .filter(x => x.used);
        this.deleteModerationBlockedDialog = true;
        this.deleteModerationDialog = false;
        this.deleteModerationIndex = null;
        return;
      }

      this.profile.content_moderation.categories.splice(idx, 1);
      this.deleteModerationDialog = false;
      this.deleteModerationIndex = null;
    },

    moderationPanelTitle(cfg, idx) {
      return cfg.name || `${this.$vuetify.lang.t('$vuetify.gpt_profile.content_moderation.category_label')} ${idx + 1}`;
    },

    externalPanelTitle(cfg, idx) {
      return cfg.name || `${this.$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.external_config_name')} ${idx + 1}`;
    },

    onDetectionRulesChange(idx, e) {
      const arr = Array.isArray(e) ? e.map(x => String(x).trim()).filter(Boolean) : [];
      this.$set(this.profile.content_moderation.categories[idx], "detection_rules", arr);
    },

    removeDetectionRule(idx, item) {
      const list = (this.profile.content_moderation.categories[idx]?.detection_rules) || [];
      const i = list.indexOf(item);
      if (i !== -1) list.splice(i, 1);
      this.$set(this.profile.content_moderation.categories[idx], "detection_rules", list);
    },

    addExternalConfig() {
      const newCfg = {
        _tmpId: Date.now(),
        name: '',
        redirection_message: '',
        redirection_url: '',
        redirection_delay_seconds: 5,
        auto_redirect: false,
        shareable_info: [],
        redirect_triggering_categories: []
      };

      if (!Array.isArray(this.profile.external_system_configs)) {
        this.$set(this.profile, 'external_system_configs', []);
      }

      this.profile.external_system_configs.push(newCfg);

      this.$nextTick(() => {
        const newIndex = this.profile.external_system_configs.length - 1;
        this.expandedExternalPanel = newIndex;
      });
    },

    confirmExternalDelete(idx) {
      this.deleteExternalIndex = idx;
      this.deleteExternalDialog = true;
    },

    deleteExternalConfirmed() {
      const idx = this.deleteExternalIndex;
      if (idx == null) return;

      if (!Array.isArray(this.profile.external_system_configs)) {
        this.deleteExternalDialog = false;
        this.deleteExternalIndex = null;
        return;
      }

      this.profile.external_system_configs.splice(idx, 1);
      this.deleteExternalDialog = false;
      this.deleteExternalIndex = null;
    },

    validateExternalConfigFields(idx) {
      const cfg = this.profile?.external_system_configs?.[idx];
      if (!cfg) return;
    },

    addResponseFilter() {
      const newFilter = {
        _tmpId: Date.now(),
        pattern: '',
        type: RESPONSE_FILTER_TYPES.PLAIN,
        is_case_sensitive: false,
        replace_with: ''
      };

      if (!Array.isArray(this.profile.response_filters)) {
        this.$set(this.profile, 'response_filters', []);
      }

      this.profile.response_filters.push(newFilter);

      this.$nextTick(() => {
        const newIndex = this.profile.response_filters.length - 1;
        this.expandedResponseFilterPanel = newIndex;
      });
    },

    confirmResponseFilterDelete(idx) {
      this.deleteResponseFilterIndex = idx;
      this.deleteResponseFilterDialog = true;
    },

    deleteResponseFilterConfirmed() {
      const idx = this.deleteResponseFilterIndex;
      if (idx == null) return;

      if (!Array.isArray(this.profile.response_filters)) {
        this.deleteResponseFilterDialog = false;
        this.deleteResponseFilterIndex = null;
        return;
      }

      this.profile.response_filters.splice(idx, 1);
      this.deleteResponseFilterDialog = false;
      this.deleteResponseFilterIndex = null;
    },

    responseFilterPanelTitle(filter, idx) {
      const pattern = String(filter.pattern || '').trim();
      if (!pattern) {
        return `${this.$vuetify.lang.t('$vuetify.gpt_profile.response_filters.filter_label')} ${idx + 1}`;
      }
      const replacement = String(filter.replace_with || '').trim();
      const target = replacement || this.$vuetify.lang.t('$vuetify.gpt_profile.response_filters.removed_label');
      return `${pattern} → ${target}`;
    },

    responseFilterTypeLabel(type) {
      const key = type === RESPONSE_FILTER_TYPES.REGEX ? 'type_regex_label' : 'type_plain_label';
      return this.$vuetify.lang.t(`$vuetify.gpt_profile.response_filters.${key}`);
    },

    responseFilterPatternRules(idx) {
      return [
        v => !!String(v || '').trim() || this.$vuetify.lang.t('$vuetify.gpt_profile.response_filters.pattern_required_message'),
      ];
    },


    onShareableInfoChange(idx, e) {
      const arr = Array.isArray(e) ? e.map(x => String(x).trim()).filter(Boolean) : [];
      this.$set(this.profile.external_system_configs[idx], "shareable_info", arr);
      this.validateExternalConfigFields(idx);
    },

    onRedirectCategoriesChange(idx, e) {
      const arr = Array.isArray(e) ? e.map(x => String(x).trim()).filter(Boolean) : [];
      const filtered = arr.filter(a => this.availableCategoryOptionsFor(idx).includes(a));
      this.$set(this.profile.external_system_configs[idx], "redirect_triggering_categories", filtered);

      this.validateExternalConfigFields(idx);

      this.$nextTick(() => {
        const refName = `externalRedirect_${idx}`;
        const ref = this.$refs[refName];
        if (ref && typeof ref.validate === 'function') {
          try { 
            ref.validate(true); 
          } catch (e) { 
            /* ignore */
          }
        }

        if (this.countEmptyRedirects() <= 1) {
          this.clearOtherExternalValidationErrors(idx);
        }
      });
    },

    clearOtherExternalValidationErrors(currentIdx) {
      (this.profile.external_system_configs || []).forEach((cfg, i) => {
        if (i === currentIdx) return;
        const refName = `externalRedirect_${i}`;
        const ref = this.$refs[refName];
        if (!ref) return;
        const callOn = (r) => {
          if (r && typeof r.resetValidation === 'function') {
            try { 
              r.resetValidation();
            } catch(e) {
              // ignore
            }
          }
          if (r && typeof r.validate === 'function') {
            try { 
              r.validate(true); 
            } catch(e) {
              // ignore
            }
          }
        };
        if (Array.isArray(ref)) {
          ref.forEach(callOn);
        } else {
          callOn(ref);
        }
      });
    },

    async fetchCategoryOptions() {
      this.isLoadingCategories = true;
      try {
        const resp = await this[GET_DEFAULT_CATEGORIES]({ project_id: this.$route.query.pid });

        const apiCats = Array.isArray(resp?.data?.detail?.categories)
          ? resp.data.detail.categories.map(item => item.name)
          : [];

        this.fetchedCategoryOptions = apiCats.length
          ? apiCats.map(String).filter(Boolean)
          : [...(this.fallbackCategories || [])];
      } catch {
        this.fetchedCategoryOptions = [...(this.fallbackCategories || [])];
      } finally {
        this.isLoadingCategories = false;
      }
    },

    async fetchShareableFields() {
      this.isLoadingShareable = true;
      try {
        const resp = await this[FETCH_SHAREABLE_DATA]({ project_id: this.$route.query.pid });

        const fieldsFromStore = this.shareableState?.fields;
        const apiFields = fieldsFromStore ?? resp?.data?.detail?.fields ?? {};

        const keys = Object.keys(apiFields);

        this.fetchedShareableFieldOptions = keys.length
          ? keys
          : Object.keys(this.fallbackShareableInfo || {});
          
      } catch (err) {
        this.fetchedShareableFieldOptions = Object.keys(this.fallbackShareableInfo || {});
      } finally {
        this.isLoadingShareable = false;
      }
    },

    isCategoryUsedInExternal(catName) {
      if (!catName) return false;
      const externalConfigs = this.profile.external_system_configs || [];
      return externalConfigs.some(cfg =>
        Array.isArray(cfg.redirect_triggering_categories) &&
        cfg.redirect_triggering_categories.includes(catName)
      );
    },

    availableCategoryOptionsFor(cfgIdx) {
      const localFromModeration = (this.profile?.content_moderation?.categories || [])
        .map(c => (c && c.name ? String(c.name).trim() : ''))
        .filter(Boolean);

      const usedByOthers = new Set();
      (this.profile?.external_system_configs || []).forEach((cfg, i) => {
        if (i === cfgIdx) return;
        (cfg.redirect_triggering_categories || []).forEach(s => {
          const t = String(s || '').trim();
          if (t) usedByOthers.add(t);
        });
      });

      return localFromModeration.filter(c => !usedByOthers.has(c));
    },

    countEmptyRedirects() {
      return (this.profile.external_system_configs || []).reduce((acc, cfg) => {
        const len = Array.isArray(cfg.redirect_triggering_categories) ? cfg.redirect_triggering_categories.filter(Boolean).length : 0;
        return acc + (len === 0 ? 1 : 0);
      }, 0);
    },

    moderationNameRules(idx) {
      const rules = [];

      rules.push(v => !!String(v || '').trim() || this.$vuetify.lang.t('$vuetify.gpt_profile.content_moderation.name_required_message'));

      rules.push(v => {
        const name = String(v || '').trim().toLowerCase();
        if (!name) return true;

        let duplicate = (this.profile?.content_moderation?.categories || [])
          .some((category, i) => {
            if (i === idx) return false;
            return category && category.name && String(category.name).trim().toLowerCase() === name;
          });

        const errMsg = this.$vuetify.lang.t('$vuetify.gpt_profile.content_moderation.name_unique_message');
        return !duplicate || errMsg;
      });

      return rules;
    },

    externalShareableRules(idx) {
      return [
        v => Array.isArray(v) && v.length > 0 || this.$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.shareable_info_required')
      ];
    },

    externalRedirectUrlRules(idx) {
      return [
        v => !!String(v || '').trim() || this.$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.redirection_url_required'),
        v => {
          if (!v) return true;
          try {
            const url = new URL(v);
            const DOMAIN_PATTERN = /^(?!-)[A-Za-z\d]([A-Za-z\d-]{0,61}[A-Za-z\d])?(\.[A-Za-z\d]([A-Za-z\d-]{0,61}[A-Za-z\d])?)*\.[A-Za-z]{2,63}$/;
            return (['http:', 'https:'].includes(url.protocol) && DOMAIN_PATTERN.test(url.hostname)) ||
                  this.$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.redirection_url_invalid');
          } catch {
            return this.$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.redirection_url_invalid');
          }
        }
      ];

    },

    externalRedirectCategoriesRules(idx) {
      return [
        v => {
          const arr = Array.isArray(v) ? v.filter(Boolean) : [];
          const emptyCount = this.countEmptyRedirects();
          const isCurrentEmpty = arr.length === 0;

          if (isCurrentEmpty && emptyCount > 1) {
            const firstEmptyIdx = (this.profile.external_system_configs || []).findIndex(c =>
              !(Array.isArray(c.redirect_triggering_categories) && c.redirect_triggering_categories.filter(Boolean).length > 0)
            );
            if (firstEmptyIdx === idx) {
              return true;
            }
            return this.$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.max_one_empty_trigger_allowed');
          }
          return true;
        }
      ];
    },

    externalConfigNameRules(idx) {
      return [
        v => !!String(v || '').trim() || this.$vuetify.lang.t('$vuetify.gpt_profile.external_system_config.config_name_required')
      ];

    },

    keyForCategory(item, idx) {
      if (!item || typeof item !== 'object') return `__idx_${idx}`;
      if (item.id !== undefined && item.id !== null) return String(item.id);
      if (item._tmpId !== undefined && item._tmpId !== null) return String(item._tmpId);
      return `__idx_${idx}`;
    },

    onCategoryNameChange(idx, newName) {
      if (
        !this.profile ||
        !Array.isArray(this.profile.content_moderation?.categories)
      ) return;

      const name =
        typeof newName === 'string'
          ? newName.trim().toLowerCase()
          : '';

      if (!name) return;

      const cfg = this.profile.content_moderation.categories[idx] || {};

      const previousCategory =
        typeof cfg._last_category_name === 'string'
          ? cfg._last_category_name
          : null;

      const categoryChanged = previousCategory !== name;

      const existingKeywords = Array.isArray(cfg.keywords)
        ? cfg.keywords.map(k => String(k).trim()).filter(Boolean)
        : [];

      const existingRules = Array.isArray(cfg.detection_rules)
        ? cfg.detection_rules.map(r => String(r).trim()).filter(Boolean)
        : [];

      const existingSeverity =
        cfg.severity_actions && typeof cfg.severity_actions === 'object'
          ? { ...cfg.severity_actions }
          : { low: 'no_action', medium: 'no_action', high: 'no_action' };

      const defaultSource =
        Array.isArray(this.defaultCategoriesState) &&
        this.defaultCategoriesState.length
          ? this.defaultCategoriesState.find(
              c =>
                c &&
                c.name &&
                String(c.name).trim().toLowerCase() === name
            )
          : null;

      if (!defaultSource) {
        this.$set(
          this.profile.content_moderation.categories[idx],
          '_last_category_name',
          name
        );
        return;
      }

      const defaultKeywords = Array.isArray(defaultSource.keywords)
        ? defaultSource.keywords.map(k => String(k).trim()).filter(Boolean)
        : [];

      const defaultRules = Array.isArray(defaultSource.detection_rules)
        ? defaultSource.detection_rules.map(r => String(r).trim()).filter(Boolean)
        : [];

      const defaultSeverity =
        defaultSource.severity_actions &&
        typeof defaultSource.severity_actions === 'object'
          ? defaultSource.severity_actions
          : { low: 'no_action', medium: 'no_action', high: 'no_action' };

      const finalKeywords = categoryChanged
        ? defaultKeywords
        : Array.from(new Set([...existingKeywords, ...defaultKeywords]));

      const finalRules = categoryChanged
        ? defaultRules
        : Array.from(new Set([...existingRules, ...defaultRules]));

      const finalSeverity = categoryChanged
        ? defaultSeverity
        : Object.assign({}, defaultSeverity, existingSeverity);

      this.$set(this.profile.content_moderation.categories, idx, {
        ...cfg,
        name,
        keywords: finalKeywords,
        detection_rules: finalRules,
        severity_actions: finalSeverity,
        _last_category_name: name
      });
    },

    onCategoryNameFocus(idx) {
      const cat = this.profile?.content_moderation?.categories?.[idx] || {};
      const key = this.keyForCategory(cat, idx);
      const name = cat?.name ? String(cat.name).trim() : '';
      this.$set(this.originalCategoryNames, key, name);
    },

    onCategoryNameBlur(idx) {
      if (!Array.isArray(this.profile?.content_moderation?.categories)) return;
      const cat = this.profile.content_moderation.categories[idx];
      if (!cat) return;

      const key = this.keyForCategory(cat, idx);
      const oldName = (this.originalCategoryNames && this.originalCategoryNames[key]) ? String(this.originalCategoryNames[key]).trim() : null;
      const newName = cat?.name ? String(cat.name).trim() : '';

      if (this.originalCategoryNames && this.originalCategoryNames[key]) {
        this.$delete(this.originalCategoryNames, key);
      }

      if (!oldName || oldName === newName) return;

      const duplicate = (this.profile?.content_moderation?.categories || [])
        .some((c, i) => {
          if (i === idx) return false;
          return (c && c.name && String(c.name).trim() === newName);
        });

      if (duplicate) {
        this.$set(this.profile.content_moderation.categories[idx], 'name', oldName);
        this.$nextTick(() => {
          this.$vuetify && this.$vuetify.toast ? this.$vuetify.toast(this.$vuetify.lang.t ? this.$vuetify.lang.t('$vuetify.gpt_profile.content_moderation.name_unique_message') : 'Name already in use') : window.alert(this.$vuetify.lang.t ? this.$vuetify.lang.t('$vuetify.gpt_profile.content_moderation.name_unique_message') : 'Name already in use');
        });
        return;
      }

      this.removeCategoryNameFromExternal(oldName);
    },

    removeCategoryNameFromExternal(oldName) {
      if (!oldName) return;
      (this.profile.external_system_configs || []).forEach((cfg, cfgIdx) => {
        if (!Array.isArray(cfg.redirect_triggering_categories)) return;
        const updated = cfg.redirect_triggering_categories
          .map(s => String(s).trim())
          .filter(s => s && s !== oldName);
        this.$set(this.profile.external_system_configs[cfgIdx], 'redirect_triggering_categories', updated);
      });
    },

    triggerManualBlur(_idx, event) {
      event.target.blur();
    },

    redirectDelayRules() {
      return [
        v => (v >= 1) || this.$vuetify.lang.t('$vuetify.gpt_profile.min_1')
      ]
    },
    async fetchAllProfiles() {
      if (this.isLoadingProfiles) return;
      
      this.isLoadingProfiles = true;
      
      try {
        const projectId = this.$route.query.pid;
        
        const response = await this.GET_GPT_PROFILES({
          project_id: projectId,
          start: 0,
          size: 100
        });
        
        const profiles = response.data?.detail?.response?.profiles || [];
        
        this.availableProfileTagsOptions = profiles
          .filter(profile => 
            Array.isArray(profile.access_tags) && profile.access_tags.length > 0
          )
          .map(profile => ({
            label: profile.name,
            tags: profile.access_tags,
            profileId: profile.id
          }));
            
      } catch (error) {
        console.error('Error fetching profiles:', error);
        this.availableProfileTagsOptions = [];
      } finally {
        this.isLoadingProfiles = false;
      }
    },

    async fetchGroupInvitations() {
      if (this.isLoadingInvitations) return;
      
      this.isLoadingInvitations = true;
      
      try {
        const projectId = this.$route.query.pid;
        const response = await this.GET_PUBLIC_INVITATIONS_LIST({ projectId });
        const invitations = response.data?.invitations || [];
        this.groupInvitationOptions = invitations
          .filter(inv => inv.status === 'active')
          .map(inv => ({
            id: inv.id,
            jti: inv.jti,
            title: inv.title,
            displayLabel: `${inv.jti}`,
            tags: inv.tags || []
          }));
        
        this.$emit('group-invitations-updated', this.groupInvitationOptions);
        // Populate tags on component load if invitation is already selected
        this.populateInvitationTags();
            
      } catch (error) {
        console.error('Error fetching invitations:', error);
        this.groupInvitationOptions = [];
      } finally {
        this.isLoadingInvitations = false;
      }
    },

    removeProfileTag(tag) {
      const tags = this.profile.selected_profile_tags;
      if (!Array.isArray(tags)) return;
      
      const index = tags.indexOf(tag);
      if (index > -1) {
        tags.splice(index, 1);
      }
    },

    resetPreviewIfNeeded() {
      if (this.isPreviewMode) {
        this.isPreviewMode = false;
        this.displayedLiffUrl = this.originalLiffUrl;
      }
    },

    resetPreviewAndEmitTags() {
      this.resetPreviewIfNeeded();
      this.emitProfileTagsChange();
    },

    async copyLiffUrl() {
      const url = this.displayedLiffUrl;
      if (!url) return;
      
      try {
        await navigator.clipboard.writeText(url);
        this.$store.dispatch(
          `alert/${ADD_ALERT}`,
          {
            message: this.$vuetify.lang.t("$vuetify.gpt_profile.liff_endpoint_url_config.url_copied_to_clipboard"),
            color: 'success',
          },
          { root: true }
        );
      } catch (err) {
        console.error('Failed to copy URL:', err);
        if (this.$vuetify?.toast) {
          this.$vuetify.toast('Failed to copy URL');
        }
      }
    },

    tagSelected(tag) {
      return this.selectedTagsArray.includes(tag);
    },

    ProfileToggleTag(tag) {
      if (!Array.isArray(this.profile.selected_profile_tags)) {
        this.$set(this.profile, 'selected_profile_tags', []);
      }

      const tags = this.profile.selected_profile_tags;
      const index = tags.indexOf(tag);
      
      if (index > -1) {
        tags.splice(index, 1);
      } else {
        tags.push(tag);
      }
    },

    truncateProfileName(name) {
      const MAX_LENGTH = 30;
      if (!name || name.length <= MAX_LENGTH) return name;
      return `${name.substring(0, MAX_LENGTH)}...`;
    },

    isProfileNameTruncated(name) {
      return name && name.length > 30;
    },
    async createAndSelectInvitation() {
      if (this.isCreatingInvitation) return;

      this.isCreatingInvitation = true;

      try {
        const projectId = this.$route.query.pid;

        const response = await this.GENERATE_PUBLIC_INVITATION_URL({ projectId });

        const invitationCode = response?.data?.code;
        if (!invitationCode) {
          console.error('Invitation code not returned from API:', response);
          return;
        }

        await this.fetchGroupInvitations();

        const newInvitation = this.groupInvitationOptions.find(
          inv => inv.jti === invitationCode
        );

        if (!newInvitation) {
          console.error(
            'Generated invitation not found in invitation list. invitationCode:',
            invitationCode
          );
          return;
        }

        if (!this.profile.liff_endpoint_url_config) {
          this.$set(this.profile, 'liff_endpoint_url_config', {
            liff_id: '',
            group_invitation_token: '',
            base_url: ''
          });
        }

        this.$set(
          this.profile.liff_endpoint_url_config,
          'group_invitation_token',
          newInvitation.jti
        );

      } catch (error) {
        console.error('Failed to create and select invitation:', error);
      } finally {
        this.isCreatingInvitation = false;
      }
    },
    togglePreview() {
      if (this.isPreviewMode) {
        // Undo
        this.isPreviewMode = false;
        this.displayedLiffUrl = this.originalLiffUrl;
      } else {
        // Preview
        this.isPreviewMode = true;
        this.displayedLiffUrl = this.generateLiffUrl();
      }
    },
    
    generateLiffUrl() {
      const config = this.profile.liff_endpoint_url_config;

      const baseUrl = config?.base_url || this.defaultBaseUrl;
      const liffId = config?.liff_id || '';
      const invitationToken = config?.group_invitation_token || '';
      
      const projectId = this.$route.query.pid;
      const profileId = this.profile.id;
      
      const generatedUrl = `${baseUrl}/line?projectId=${projectId}&profileId=${profileId}&invitationToken=${invitationToken}&LIFF_ID=${liffId}`;

      this.$set(this.profile, 'liff_endpoint_url', generatedUrl);
      
      return generatedUrl;
    },
    
    emitProfileTagsChange() {
      const config = this.profile.liff_endpoint_url_config;
      
      if (!config?.group_invitation_token) {
        return;
      }
      
      const selectedInvitation = this.groupInvitationOptions.find(
        inv => inv.jti === config.group_invitation_token
      );
      
      if (!selectedInvitation) {
        return;
      }
      
      this.$emit('profile-tags-change', {
        invitationId: selectedInvitation.id,
        tags: Array.isArray(this.profile.selected_profile_tags) 
          ? this.profile.selected_profile_tags 
          : []
      });
    },
    populateInvitationTags() {
      const config = this.profile.liff_endpoint_url_config;
      
      if (!config?.group_invitation_token) {
        this.$set(this.profile, 'selected_profile_tags', []);
        return;
      }
      
      const selectedInvitation = this.groupInvitationOptions.find(
        inv => inv.jti === config.group_invitation_token
      );
      
      if (!selectedInvitation) {
        this.$set(this.profile, 'selected_profile_tags', []);
        return;
      }
      
      const invitationTags = Array.isArray(selectedInvitation.tags) 
        ? selectedInvitation.tags 
        : [];
      
      this.$set(this.profile, 'selected_profile_tags', [...invitationTags]);
    },
    clearLiffConfig() {
      const { liff_endpoint_url_config } = this.profile;

      if (liff_endpoint_url_config) {
        this.$set(liff_endpoint_url_config, 'liff_id', '');
        this.$set(liff_endpoint_url_config, 'group_invitation_token', '');
      }

      if (this.profile.liff_endpoint_url) {
        this.$set(this.profile, 'liff_endpoint_url', '');
      }
    },

    ensureBaseUrl() {
      const baseUrl = this.defaultBaseUrl || '';

      if (!this.profile.liff_endpoint_url_config) {
        this.$set(this.profile, 'liff_endpoint_url_config', {
          liff_id: '',
          group_invitation_token: '',
          base_url: baseUrl
        });
        return;
      }

      if (!this.profile.liff_endpoint_url_config.base_url) {
        this.$set(this.profile.liff_endpoint_url_config, 'base_url', baseUrl);
      }
    },
    onProviderChange() {
      this.profile.model = '';
      this.applySupportedTools();
    },
    onModelChange() {
      this.isManualModelChange = true;
      this.modelFieldError = "";
      this.currentModelMaxOutputTokenSize = null;

      if (this.$refs.temperatureField) {
        this.$refs.temperatureField.resetValidation();
      }
      if (this.$refs.topPField) {
        this.$refs.topPField.resetValidation();
      }

      this.applySupportedTools();
    },
    applySupportedTools() {
      if (this.rememberedTools === null) {
        this.rememberedTools = Array.isArray(this.profile.tools) ? [...this.profile.tools] : [];
      }

      const supported = [];
      if (this.isWebSearchSupportedByModel && this.rememberedTools.includes('web_search')) {
        supported.push('web_search');
      }
      if (this.isCodeExecutionSupportedByModel && this.rememberedTools.includes('code_execution')) {
        supported.push('code_execution');
      }
      this.$set(this.profile, 'tools', supported);
    },
    onToolsChange() {
      // rememberedTools can still be null here - the user can reach the select before applySupportedTools() has ever run (e.g. web_search is already visible while code_execution's model details are still loading).
      // Seed it from profile.tools first, same as applySupportedTools, so a tool that isn't visible yet doesn't get treated as deselected and lost.
      if (this.rememberedTools === null) {
        this.rememberedTools = Array.isArray(this.profile.tools) ? [...this.profile.tools] : [];
      }
      const selected = Array.isArray(this.profile.tools) ? this.profile.tools : [];
      const remembered = new Set(this.rememberedTools);
      this.modelTools.forEach(({ value }) => {
        if (selected.includes(value)) {
          remembered.add(value);
        } else {
          remembered.delete(value);
        }
      });
      this.rememberedTools = Array.from(remembered);
    },
    // Method to parse params and assign defaults safely
    updateHyperparams(modelDetails, isModelChange = false) {
      if (!modelDetails || !modelDetails.hyperparams) {
        // Reset hyperparams to defaults if modelDetails or hyperparams are missing
        this.temperatureMeta = { min: 0, max: 2, default: 0, required: false };
        this.topPMeta = { min: 0, max: 1, default: 1, required: false };

        if (isModelChange || validations.isEmpty(this.profile.temperature)) {
          this.$set(this.profile, 'temperature', this.temperatureMeta.default);
        }
        if (isModelChange || validations.isEmpty(this.profile.top_p)) {
          this.$set(this.profile, 'top_p', this.topPMeta.default);
        }

        return;
      }

      const hyperparams = modelDetails.hyperparams;

      // Temperature Processing
      if (hyperparams?.temperature) {
        const { min, max, default: defaultVal, required } = hyperparams?.temperature || {};
        this.temperatureMeta.min = min ?? 0;
        this.temperatureMeta.max = max ?? 2;
        this.temperatureMeta.default = defaultVal ?? this.temperatureMeta?.min ?? 0;
        this.temperatureMeta.required = required ?? false;

        // On every manual model change, we reset to the model's default bounds.
        if (isModelChange || validations.isEmpty(this.profile.temperature)) {
          this.$set(this.profile, 'temperature', this.temperatureMeta.default);
        }
      } else {
        this.temperatureMeta = { min: 0, max: 2, default: 0, required: false };
        if (isModelChange || validations.isEmpty(this.profile.temperature)) {
          this.$set(this.profile, 'temperature', this.temperatureMeta.default);
        }
      }

      // Top P Processing
      if (hyperparams.top_p) {
        const { min, max, default: defaultVal, required } = hyperparams?.top_p || {};
        this.topPMeta.min = min ?? 0;
        this.topPMeta.max = max ?? 1;
        this.topPMeta.default = defaultVal ?? this.topPMeta?.min ?? 1;
        this.topPMeta.required = required ?? false;

        // On every manual model change, we reset to the model's default bounds.
        if (isModelChange || validations.isEmpty(this.profile.top_p)) {
          this.$set(this.profile, 'top_p', this.topPMeta.default);
        }
      } else {
        this.topPMeta = { min: 0, max: 1, default: 1, required: false };
        if (isModelChange || validations.isEmpty(this.profile.top_p)) {
          this.$set(this.profile, 'top_p', this.topPMeta.default);
        }
      }
    },
  },
};
</script>

<style scoped>
.datasets-tag-text {
  padding-bottom: 0 !important;
}

.no-dropdown-icon >>> .v-input__append-inner .v-icon {
  display: none !important;
}

.disabled-tag {
  pointer-events: none;
  opacity: 0.4 !important;
}

.v-chip.clickable {
  cursor: pointer;
  transition: all 0.2s;
}

.v-chip.clickable:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.v-chip.disabled-tag:hover {
  transform: none;
  box-shadow: none;
}

.text-grey-600 {
  color: rgba(0,0,0,0.6) !important;
}

.border-t {
  border-top: 1px solid #e0e0e0;
}

.header-row {
  border-bottom: 1px solid #e0e0e0;
}

.data-row {
  border-bottom: 1px solid #f5f5f5;
}

.dataset-name-column {
  min-width: 180px;
  max-width: 180px;
  flex-shrink: 0;
  padding-right: 16px;
}

.tags-column {
  flex: 1;
  min-width: 0;
}

.dataset-name-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tooltip-text {
  font-size: 0.875rem;
}

.tag-badges {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 6px;
}

.disabled-dataset {
  opacity: 0.6;
  background-color: #f5f5f5;
}

.disabled-text {
  color: #9e9e9e !important;
}

.info-icon {
  display: inline-block;
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  border-radius: 50%;
  background-color: #e0e0e0;
  color: #333;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  margin-left: 8px;
  pointer-events: auto;
}
.textarea-with-tooltip {
  position: relative;
}
.textarea-with-tooltip .info-icon {
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: 2;
}
.v-application--is-ltr .v-textarea.v-text-field--enclosed .v-text-field__slot {
  padding-right: 30px;
}

.field-row {
  display: flex;
  align-items: center;
}

.severity-row {
  display: flex;
  gap: 18px;
  align-items: flex-start;
  justify-content: flex-start;
  margin-top: 6px;
}

.sev-cell {
  display: flex;
  flex-direction: column;
  width: 32%; 
  min-width: 170px;
}

.sev-label {
  font-weight: 700;
  font-size: 12px;
  color: rgba(0,0,0,0.7);
  margin-bottom: 6px;
  letter-spacing: 1px;
}

@media (max-width: 900px) {
  .sev-cell { width: 100%; min-width: auto; }
  .severity-row { flex-direction: column; gap: 12px; }
}
.external-config .field-row,
.response-filters-section .field-row {
  margin-bottom: 12px;
}

.v-chip.clickable {
  cursor: pointer;
  transition: all 0.15s;
}

.v-chip.clickable:hover {
  transform: translateY(-1px);
}

.info-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  margin-top: 6px;
}

.content-moderation-section >>> .v-input__icon--clear,
.external-config >>> .v-input__icon--clear,
.profile-liff-config >>> .v-input__icon--clear {
  padding-bottom: 5px !important;
  padding-top: 0 !important;
}

.response-filter-title {
  max-width: 60%;
  flex: 0 1 auto;
  min-width: 0;
}

.response-filter-header {
  min-width: 0;
  width: 100%;
}

@media (max-width: 600px) {
  .response-filter-title {
    max-width: 35%;
  }
  
  .response-filter-header .v-chip {
    margin-left: 4px !important;
  }
  
  .response-filter-header .v-btn {
    flex-shrink: 0;
  }
}

.response-filter-control-box {
  display: flex;
  align-items: center;
  min-height: 56px;
}

.response-filter-control-box .v-checkbox {
  flex: 0 0 auto;
}

.response-filter-type-box {
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  margin-top: -6px;
  margin-left: 4px;
  margin-bottom: 12px;
}

@media (max-width: 960px) {
  .response-filter-type-box {
    margin-left: 0;
    margin-top: 0;
    margin-bottom: 20px;
  }
}

.response-filter-type-toggle .v-btn {
  min-width: 84px;
  text-transform: capitalize;
}

.shareable-info-select >>> .v-input__icon--clear,
.redirect-categories-select >>> .v-input__icon--clear,
.profile-access-tags-combobox >>> .v-input__icon--clear {
  padding-top: 13px !important;
  padding-bottom: 5px !important;
}
.v-card .v-card__text >>> .v-input--is-disabled {
  opacity: 0.6;
}

.thin-scroll {
  display: block;
  overflow-x: auto;
  white-space: nowrap;
  padding-left: 2px;
}

.thin-scroll::-webkit-scrollbar {
  height: 4px;
}

.thin-scroll::-webkit-scrollbar-thumb {
  background: #bbb;
  border-radius: 2px;
}

.thin-scroll::-webkit-scrollbar-track {
  background: transparent;
}

::v-deep(.v-autocomplete.v-select.v-input--is-focused .url-chip~input){
 flex: 1 1 100%;
 box-sizing: border-box;
}

.header-row,
.data-row {
  display: grid !important;
  grid-template-columns: 180px 1fr;
  column-gap: 16px;
  align-items: flex-start;
}
.profile-name-column {
  white-space: nowrap;
  overflow: hidden;
}
.profile-name-text {
  line-height: 28px;
}
.data-row:hover {
  background: #fafafa;
  border-radius: 6px;
}

.info-notice {
  color: #555;
  font-size: 14px;
}
.info-notice .v-icon {
  align-self: flex-start;
  margin-top: 2px;
}
.info-notice-text {
  align-self: flex-start;
  flex: 1 1 0;
  min-width: 0;
}

.background-ask-error {
  flex-basis: 100%;
}

</style>
